import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ErrorApi, fetchAutenticado } from '../api/cliente';
import type {
  AccionOrden, Opcion, Orden, OrdenDetalle, Pagina, PreventivoDeLaOrden, Refaccion,
  RenglonCotizacion, ResumenOrdenes, ServicioReparacion, SolicitudAlmacen, Sucursal, Tot,
  AvisoDeSurtido, ServiciosPactados, OpcionResponsable, DependeDe,
} from '../api/tipos';
import { useSesion } from '../sesion';
import { FormularioTot, FichaTot, EtiquetaEstatusTot, dinero as dineroDelVale } from './Tot';
import {
  Cargando, Dialogo, Error, Falla, Faltantes, ImagenProtegida, motivoDeFalla, Paginacion,
  Ordenable, useOrden,
} from '../componentes/comunes';
import { Distintivo } from '../etiquetas';
import { folioOT } from '../folio';
import CargaMasiva from './CargaMasiva';
import { BuscadorRefaccion } from '../componentes/BuscadorRefaccion';
import { aligerar } from '../imagenes';

/**
 * Una evidencia en la rejilla: miniatura si es imagen, enlace si no.
 *
 * Karin: «en ordendes, en evidencias, poder adjuntar archivos de formato
 * docx, pdf». Hasta ahora todo se pintaba con `ImagenProtegida`, porque
 * todo era una foto; un PDF o un Word por ahí sólo consiguen un «No se
 * pudo cargar».
 *
 * Los documentos se bajan en vez de dibujarse —el navegador no puede
 * dibujar un Word, y la API ya los sirve como descarga— así que lo que
 * se enseña es su nombre y el formato, para poder distinguir dos
 * adjuntos sin abrirlos.
 */
function Adjunto({ ordenId, evidencia, onQuitar }: {
  ordenId: string;
  evidencia: { id: string; nombre: string; mimeType: string | null };
  onQuitar?: () => void;
}) {
  const esImagen = (evidencia.mimeType ?? '').startsWith('image/');

  return (
    <figure style={{ width: 130 }}>
      {esImagen ? (
        <ImagenProtegida
          src={`/api/v1/ordenes/${ordenId}/evidencias/${evidencia.id}`}
          alt={evidencia.nombre}
          style={{ width: '100%', borderRadius: 8, display: 'block' }}
        />
      ) : (
        <button
          className="enlace"
          style={{
            width: '100%', height: 90, borderRadius: 8,
            border: '1px dashed var(--border)', textAlign: 'center',
          }}
          onClick={() => {
            void api.descargar(
              `/ordenes/${ordenId}/evidencias/${evidencia.id}`,
              undefined,
              evidencia.nombre,
            );
          }}
        >
          {evidencia.mimeType === 'application/pdf' ? 'PDF' : 'Documento'}
        </button>
      )}
      <figcaption className="ayuda" style={{ wordBreak: 'break-all' }}>
        {evidencia.nombre}
        {onQuitar && (
          <button className="enlace" onClick={onQuitar}>Quitar</button>
        )}
      </figcaption>
    </figure>
  );
}

/**
 * De quién está esperando la orden, debajo de su estatus.
 *
 * Karin: «cuando almacen recibe solicitudes y pasa a compras y luego a
 * autorizar para llegar a contabilidad y pagar, el estatus de la orden
 * sigue siendo en espera de refacciones, ¿que podemos hacer para que se
 * sepa exactamente de quien esta dependiendo esa orden?».
 *
 * Va **pegado al estatus** y no en una columna aparte, que es donde se
 * lee: el problema es que «En espera de refacciones» no dice nada, así
 * que la aclaración tiene que estar justo ahí. La tabla ya trae siete
 * columnas y una octava las estrecha todas.
 *
 * Una línea y un solo pendiente. La pregunta es a quién hablarle, y lo
 * que falta completo lo enseña la ficha en «No se puede cerrar
 * todavía».
 */
function DeQuienDepende({ dependeDe }: { dependeDe: DependeDe | null }) {
  if (!dependeDe) return null;

  return (
    <div className="ayuda" style={{ marginTop: 2 }}>
      <strong>{dependeDe.area}:</strong>
      {' '}
      {dependeDe.falta}
      {' '}
      <span className="mono">({dependeDe.folio})</span>
    </div>
  );
}

/**
 * El desplegable de responsable, con los supervisores arriba.
 *
 * Karin: «sobre el responsable podemos hacer que aparezcan los
 * supervisores? segun su zona, no recuerdo si tenemos un catalogo de
 * eso». Catálogo propio no hay y no hace falta: un supervisor es un
 * usuario con ese rol, y su zona son sus regiones asignadas.
 *
 * Van agrupados y no filtrados. Quitar al resto rompería el alta de
 * todos los días —hoy el responsable suele ser quien captura, de soporte
 * operativo, y el campo arranca prellenado con esa persona— y un
 * desplegable cuyo valor por omisión no está en la lista arranca
 * enseñando un hueco. Lo que hacía falta era poder encontrarlos, no
 * esconder a los demás.
 *
 * La zona va junto al nombre: distingue a dos que se apellidan igual, y
 * sin ella «de la región elegida» es una promesa que nadie puede
 * comprobar. Se recorta a dos porque la 0025 les dio todas las zonas
 * mientras APRISA no tenga un supervisor por región, y catorce nombres
 * de estado en un `option` no dicen nada.
 *
 * Mismo patrón que `OpcionesDeTecnico` en Preventivo, a propósito: dos
 * desplegables que agrupan por lo mismo deben verse igual.
 */
function OpcionesDeResponsable({ responsables }: { responsables: OpcionResponsable[] }) {
  const supervisores = responsables.filter((r) => r.esSupervisor);
  const demas = responsables.filter((r) => !r.esSupervisor);

  const conZona = (r: OpcionResponsable) => (r.zonas.length === 0
    ? r.nombre
    : `${r.nombre} · ${r.zonas.slice(0, 2).join(', ')}`
      + (r.zonas.length > 2 ? ` y ${r.zonas.length - 2} más` : ''));

  return (
    <>
      {supervisores.length > 0 && (
        <optgroup label="Supervisores">
          {supervisores.map((r) => (
            <option key={r.id} value={r.id}>{conZona(r)}</option>
          ))}
        </optgroup>
      )}
      {demas.length > 0 && (
        <optgroup label={supervisores.length > 0 ? 'Otros usuarios' : 'Usuarios'}>
          {demas.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
        </optgroup>
      )}
    </>
  );
}

/**
 * Órdenes de trabajo.
 *
 * La pantalla no decide qué se puede hacer con una orden: la API devuelve
 * las acciones válidas para este perfil y este estado, y aquí se pintan
 * como botones. Así la pantalla no puede quedar desalineada con las
 * reglas del motor de estados.
 */
export default function Ordenes() {
  const { puede } = useSesion();
  const navegar = useNavigate();

  /**
   * Al programa preventivo, en el mes de esa visita.
   *
   * Karin: «en "preventivo" y desde ahi gestionamos esas». Para que el
   * enlace sirva tiene que aterrizar en el mes de su visita: llegar al
   * mes corriente y tener que buscar septiembre a mano convierte un
   * enlace en una pista.
   */
  const irALaVisita = (anio: number, mes: number, visita?: string) => {
    /**
     * Y con el id, el panel de esa visita se abre solo al llegar.
     *
     * Karin: «sera posible que desde una OT preventiva se llegue a su
     * vista de preventivo?». Aterrizar en el mes correcto dejaba
     * todavía ochenta renglones que leer para encontrar la sucursal.
     *
     * Sin id —la preventiva suelta, que no tiene visita que abrir— se
     * va al mes y ya, que es lo único que se puede ofrecer.
     */
    navegar(
      `/preventivo?anio=${anio}&mes=${mes}${visita ? `&visita=${visita}` : ''}`,
    );
  };

  const [bandeja, setBandeja] = useState('abiertas');
  const [busqueda, setBusqueda] = useState('');
  const [regionId, setRegionId] = useState('');
  const [tecnicoId, setTecnicoId] = useState('');
  const [prioridad, setPrioridad] = useState('');
  /**
   * Correctiva o preventiva.
   *
   * Es el tipo de la orden, no el tipo de iguala de la sucursal. Se
   * parecen al hablar y no son lo mismo: la iguala es el contrato de esa
   * tienda —full, trimestral, de apoyo— y el tipo dice a qué vino esta
   * orden. Una sucursal de iguala full tiene correctivas y preventivas.
   */
  const [tipo, setTipo] = useState('');
  const [numero, setNumero] = useState(1);
  const { orden, alClicar, limpiar: limpiarOrden } = useOrden(() => setNumero(1));

  const [pagina, setPagina] = useState<Pagina<Orden> | null>(null);
  const [resumen, setResumen] = useState<ResumenOrdenes | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [regiones, setRegiones] = useState<Opcion[]>([]);
  const [tecnicos, setTecnicos] = useState<Opcion[]>([]);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [abriendoCarga, setAbriendoCarga] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/regiones/opciones').then(setRegiones).catch(() => undefined);
    api.get<Opcion[]>('/tecnicos/opciones').then(setTecnicos).catch(() => undefined);
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Orden>>('/ordenes', {
      bandeja: bandeja || undefined,
      busqueda: busqueda || undefined,
      regionId: regionId || undefined,
      tecnicoId: tecnicoId || undefined,
      prioridad: prioridad || undefined,
      tipo: tipo || undefined,
      // Sin nada elegido no se manda: así ordena la API con el orden que
      // corresponde a cada bandeja, en vez de imponerle uno desde aquí.
      ordenarPor: orden?.por,
      sentido: orden?.sentido,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));

    api.get<ResumenOrdenes>('/ordenes/resumen').then(setResumen).catch(() => undefined);
  }, [bandeja, busqueda, regionId, tecnicoId, prioridad, tipo, numero, orden]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  const bandejas = [
    { clave: 'abiertas', etiqueta: 'Abiertas' },
    { clave: 'sin_asignar', etiqueta: 'Sin asignar', conteo: resumen?.sinAsignar },
    { clave: 'vencidas', etiqueta: 'Vencidas', conteo: resumen?.vencidas },
    { clave: 'cerradas', etiqueta: 'Cerradas' },
    { clave: '', etiqueta: 'Todas' },
  ];

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Órdenes de trabajo</h1>
          <p className="sub">
            El ciclo completo: se crea, se asigna al técnico de la zona, se
            trabaja y se cierra con evidencia.
          </p>
        </div>
        {puede('ordenes', 'crear') && (
          <>
            <button onClick={() => setAbriendoCarga(true)}>Cargar archivo</button>
            <button className="primario" onClick={() => setCreando(true)}>Nueva orden</button>
          </>
        )}
      </div>

      <nav className="submenu">
        {bandejas.map((b) => (
          <button
            key={b.clave || 'todas'}
            className={`enlace ${bandeja === b.clave ? 'activo' : ''}`}
            // El orden elegido no se arrastra de una bandeja a otra: en
            // las cerradas se ordena por fecha de cierre y esa columna ni
            // siquiera existe en las demás.
            onClick={() => { setBandeja(b.clave); setNumero(1); limpiarOrden(); }}
          >
            {b.etiqueta}
            {b.conteo !== undefined && b.conteo > 0 && (
              <span className={`etiqueta ${b.clave === 'vencidas' ? 'no' : 'si'}`}
                    style={{ marginLeft: 6 }}>
                {b.conteo}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Folio, orden de servicio, sucursal o falla…"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
          />
          <select value={regionId} onChange={(e) => { setRegionId(e.target.value); setNumero(1); }}>
            <option value="">Todas las regiones</option>
            {regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
          <select value={tecnicoId} onChange={(e) => { setTecnicoId(e.target.value); setNumero(1); }}>
            <option value="">Todos los técnicos</option>
            {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
          <select value={prioridad} onChange={(e) => { setPrioridad(e.target.value); setNumero(1); }}>
            <option value="">Todas las prioridades</option>
            <option value="urgente">Urgente</option>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>
          {/* Correctivas o preventivas. La orden guarda su tipo desde que
              se da de alta o se carga del archivo del cliente; esto no
              es el tipo de iguala de la sucursal, que es su contrato.

              Karin pidió poder ocultar las preventivas de aquí, y resulta
              que ya se podía: es este filtro. Salen por omisión a
              propósito —la bandeja es la vista de todo lo que está
              abierto, y una preventiva parada en almacén tiene que verse;
              el programa se mira por mes y sucursal, no es una bandeja de
              pendientes—. «Sólo correctivas» es para cuando estorban. */}
          <select value={tipo} onChange={(e) => { setTipo(e.target.value); setNumero(1); }}>
            <option value="">Correctivas y preventivas</option>
            <option value="correctivo">Sólo correctivas</option>
            <option value="preventivo">Sólo preventivas</option>
          </select>
        </div>

        <Error error={error} />

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            {pagina.data.length === 0 ? (
              <div className="vacio">No hay órdenes en esta bandeja.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <Ordenable por="folio" orden={orden} alClicar={alClicar}>Folio OT</Ordenable>
                    <Ordenable por="sucursal" orden={orden} alClicar={alClicar}>Sucursal</Ordenable>
                    {/* En vez de la falla, que el archivo del cliente no
                        trae y sale vacía en todo lo cargado. El equipo sí
                        viene: se saca del nombre de la tarea. No se puede
                        ordenar por él: no es una columna, sale de leer el
                        nombre de la tarea en la pantalla. */}
                    <th>Equipo</th>
                    <Ordenable por="tecnico" orden={orden} alClicar={alClicar}>Técnico</Ordenable>
                    {/* Primero lo urgente: el primer clic ordena de mayor
                        a menor prioridad, que es la pregunta que alguien
                        se hace al clicar esta columna. */}
                    <Ordenable por="prioridad" orden={orden} alClicar={alClicar} primerClic="desc">
                      Prioridad
                    </Ordenable>
                    <Ordenable por="estatus" orden={orden} alClicar={alClicar}>Estatus</Ordenable>
                    {/* En las cerradas, el límite ya no dice nada: lo que
                        se quiere saber de una orden terminada es cuándo
                        terminó. Es la misma columna con otra pregunta,
                        así que se cambia en vez de agregar una que sale
                        vacía en las demás bandejas. */}
                    <Ordenable
                      por={bandeja === 'cerradas' ? 'fecha_cierre' : 'limite'}
                      orden={orden}
                      alClicar={alClicar}
                      primerClic={bandeja === 'cerradas' ? 'desc' : 'asc'}
                    >
                      {bandeja === 'cerradas' ? 'Cerrada' : 'Límite'}
                    </Ordenable>
                  </tr>
                </thead>
                <tbody>
                  {pagina.data.map((o) => (
                    <tr key={o.id}>
                      <td className="mono">
                        <button className="enlace" onClick={() => setAbierta(o.id)}>
                          {folioOT(o.folio)}
                        </button>
                        {/* Sólo las preventivas llevan marca: la
                            correctiva es lo normal y etiquetarlas todas
                            sería ruido en cada renglón. Con el filtro
                            puesto sobra, pero mezcladas hace falta. */}
                        {o.tipo === 'preventivo' && (
                          <span className="etiqueta" style={{ marginLeft: 6 }}>preventiva</span>
                        )}
                      </td>
                      <td>
                        {o.sucursal
                          ? `${o.sucursal.determinante} — ${o.sucursal.nombre}`
                          : '—'}
                      </td>
                      <td style={{ maxWidth: 280 }}>{equipoDe(o)}</td>
                      <td>{o.tecnico?.nombre_completo ?? <span className="etiqueta no">Sin asignar</span>}</td>
                      <td><Prioridad valor={o.prioridad} /></td>
                      <td>
                        <QueEspera orden={o} />
                        <DeQuienDepende dependeDe={o.dependeDe} />
                      </td>
                      <td>
                        {bandeja === 'cerradas'
                          ? (o.fechaCierre ? fechaCorta(o.fechaCierre) : '—')
                          : o.fechaLimite
                            ? <span className={o.vencida ? 'etiqueta no' : undefined}>
                                {soloDia(o.fechaLimite)}
                              </span>
                            : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacion
              pagina={pagina.pagina}
              limite={pagina.limite}
              total={pagina.total}
              onCambiar={setNumero}
            />
          </>
        )}
      </div>

      {abierta && (
        <Ficha
          id={abierta}
          tecnicos={tecnicos}
          onCerrar={() => setAbierta(null)}
          onCambio={cargar}
          onIrALaVisita={irALaVisita}
        />
      )}

      {creando && (
        <FormularioOrden
          onCerrar={() => setCreando(false)}
          onCreada={(id) => { setCreando(false); cargar(); setAbierta(id); }}
        />
      )}

      {abriendoCarga && (
        <CargaMasiva
          onCerrar={() => setAbriendoCarga(false)}
          onCargado={cargar}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════ presentación

/** Cómo se llama en la pantalla el área que firma cada renglón. */
const NOMBRE_DEL_AREA: Record<'operaciones' | 'almacen', string> = {
  operaciones: 'Operaciones',
  almacen: 'Almacén',
};

/**
 * El permiso con el que cada área firma su parte de la cotización.
 *
 * No es el permiso del área. `almacen.autorizar` sirve además para las
 * solicitudes de almacén, y los dos roles con `operaciones.autorizar` lo
 * tienen también, así que usarlos aquí hacía que a Operaciones le
 * aparecieran las refacciones de la orden además de sus servicios. Los
 * módulos de abajo existen sólo para esto y los pone la 0036.
 */
const MODULO_DEL_AREA: Record<'operaciones' | 'almacen', string> = {
  operaciones: 'cotizacion_servicios',
  almacen: 'cotizacion_refacciones',
};

/**
 * Las áreas cuyos renglones puede firmar quien está viendo la pantalla.
 *
 * Lo usan la ficha y la bandeja, desde el mismo lugar: una bandeja que
 * lista una orden cuya ficha después no deja firmar nada es peor que no
 * listarla.
 */
export function areasQueFirma(
  puede: (m: string, a: string) => boolean,
): ('operaciones' | 'almacen')[] {
  return (['operaciones', 'almacen'] as const)
    .filter((area) => puede(MODULO_DEL_AREA[area], 'autorizar'));
}

/**
 * Cómo se lee cada estado de la orden.
 *
 * Exportado porque el preventivo lo usa para nombrar las órdenes a las
 * que se puede enlazar una solicitud anticipada: ahí hay que decir en qué
 * va cada una, y una segunda copia acabaría diciendo otra cosa.
 */
export const ETIQUETAS_ESTATUS: Record<string, string> = {
  aceptado: 'Aceptada',
  asignado: 'Asignada',
  // El respaldo dice lo mismo que el catálogo: se espera al técnico,
  // que contesta por fuera, no a que alguien cotice aquí dentro.
  espera_cotizacion: 'Espera respuesta técnico',
  espera_refacciones: 'Espera refacciones',
  /**
   * Firmas en plural, y «de áreas» para decir que no se dan aquí.
   *
   * «Espera autorización» pedía una autorización que nadie en esta
   * pantalla puede dar: lo que espera son dos firmas por renglón, en la
   * bandeja de Operaciones y en la de Almacén. Soporte veía el botón de
   * confirmar apagado y se quedaba esperando a que alguien apareciera.
   */
  espera_autorizacion: 'Espera firmas de áreas',
  /**
   * «Autorizada» a secas se leía como «el cliente autorizó», y es lo
   * contrario de lo que significa: aquí firmaron Operaciones y Almacén, y
   * el cliente no ha visto nada todavía —lo ve en `espera_cliente`—.
   * Empezar el trabajo aquí creyendo que el cliente dijo sí es trabajar
   * sin con qué respaldar el cobro.
   */
  autorizado: 'Autorizada internamente',
  espera_cliente: 'Espera respuesta del cliente',
  en_proceso: 'En proceso',
  cierre_admin: 'Cerrada',
  cancelado: 'Cancelada',
};

/**
 * El estatus, con el color que le puso el catálogo.
 *
 * El texto de respaldo se conserva por si el catálogo no responde o si
 * alguien desactivó la opción: un estatus sin nombre es una tabla llena
 * de claves con guiones bajos.
 */
function EtiquetaEstatus({ valor }: { valor: string }) {
  return (
    <Distintivo familia="estatus_interno" valor={valor} respaldo={ETIQUETAS_ESTATUS[valor]} />
  );
}

/**
 * La etiqueta que dice qué espera la orden.
 *
 * Karin: «seria útil que apareciera como "en espera de firmas" o "en
 * espera de pago" cuando sea el caso […] si queda el estatus como
 * "Espera firmas de áreas" no entendemos que falta».
 *
 * El texto lo manda la API. Aquí no se decide nada: repetir la regla en
 * la pantalla es como la bandeja y la ficha acaban diciendo cosas
 * distintas de la misma orden.
 *
 * Cuando no hay nada derivado que decir cae en `EtiquetaEstatus`, que
 * usa el color del catálogo. Las derivadas no pueden usarlo: el color
 * está atado a la clave del estatus y aquí el texto dice otra cosa, así
 * que un rojo de «espera refacciones» debajo de «Espera pago» diría una
 * tercera cosa distinta de las dos.
 */
function QueEspera({ orden }: { orden: Orden }) {
  if (!orden.enQueEspera?.derivada) return <EtiquetaEstatus valor={orden.estatus} />;

  const { texto, clase } = orden.enQueEspera;

  return <span className={clase ? `etiqueta ${clase}` : 'etiqueta'}>{texto}</span>;
}

function Prioridad({ valor }: { valor: string }) {
  const urgente = valor === 'urgente' || valor === 'alta';
  return <span className={urgente ? 'etiqueta no' : undefined}>{valor}</span>;
}

/**
 * Cómo se nombra el equipo de una orden en una lista.
 *
 * Se arma de lo que haya: área, tipo de equipo, marca y modelo salen del
 * nombre de la tarea del cliente, y no siempre están los cuatro. Cuando
 * no se pudo clasificar queda el texto libre, y si tampoco hay, la falla
 * reportada —que hoy sólo tienen las órdenes capturadas a mano—.
 */
function equipoDe(o: Orden): string {
  const partes = [o.area?.nombre, o.tipoEquipo?.nombre, o.marca?.nombre, o.modelo?.nombre]
    .filter(Boolean);

  if (partes.length > 0) return partes.join(' · ');
  return recortar(o.equipoTexto || o.descripcionFalla);
}

function recortar(texto: string | null, largo = 90): string {
  if (!texto) return '—';
  return texto.length > largo ? `${texto.slice(0, largo)}…` : texto;
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * El día, sin hora. Para la fecha límite.
 *
 * Karin: «en la ordenes la fecha limite, quitales las horas».
 *
 * La hora que salía no significaba nada: `calcularFechaLimite` le suma a
 * la creación las horas de SLA de la prioridad —72 para una media— así
 * que el «14:37» de una orden es a qué hora alguien la capturó tres días
 * antes, no una hora comprometida con el cliente. Enseñarla invita a
 * leerla como un plazo al minuto que nadie pactó, y de paso hace que dos
 * órdenes del mismo día se vean distintas sin serlo.
 *
 * Las demás fechas de esta pantalla sí conservan la hora, y es a
 * propósito: en la bitácora el orden de los eventos del día es la
 * información, y en un cierre o un acuse la hora dice cuándo pasó de
 * verdad.
 */
function soloDia(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function dinero(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

/**
 * Los renglones que este perfil tiene que autorizar, con su precio.
 *
 * Operaciones firma los servicios y Almacén las refacciones. Cada quien
 * ve sólo lo suyo: un servicio no lo firma almacén aunque tenga permiso
 * de autorizar en su módulo, porque el precio de un servicio sale de lo
 * pactado con el proveedor y eso lo sabe Operaciones.
 *
 * Se firman todos de una vez y no de uno en uno: quien revisa las
 * refacciones de una orden las mira juntas, y guardar entre cada una deja
 * la cotización medio firmada si algo falla en la tercera.
 */
function PorAutorizar({ orden, puede, alGuardar }: {
  orden: OrdenDetalle;
  puede: (m: string, a: string) => boolean;
  alGuardar: () => void;
}) {
  const areas = areasQueFirma(puede);

  const mios = orden.cotizacion.filter(
    (r) => !r.autorizadoEn && areas.includes(r.area),
  );

  const [precios, setPrecios] = useState<
    Record<string, { costo: string; venta: string; descripcion: string }>
  >({});
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  if (mios.length === 0) return null;

  /**
   * El costo arranca con lo que capturó soporte, cuando lo capturó.
   *
   * En un servicio suele venir: soporte habló con el taller y lo puso al
   * registrar. Llega prellenado y editable porque quien autoriza firma el
   * gasto y tiene que poder responder por la cifra —negoció otro precio,
   * o venía un error de dedo—. En una refacción viene vacío: no hay nada
   * capturado, el costo sale del inventario y lo pone Almacén aquí.
   */
  const capturado = (r: OrdenDetalle['cotizacion'][number]) =>
    (r.precioCosto === null ? '' : String(r.precioCosto));

  const de = (id: string) => {
    const guardado = precios[id];
    if (guardado) return guardado;

    const renglon = mios.find((r) => r.id === id);
    return {
      costo: renglon ? capturado(renglon) : '',
      venta: '',
      // La descripción arranca con lo que escribió Soporte, igual que el
      // costo: se corrige, no se vuelve a escribir desde cero.
      descripcion: renglon?.descripcion ?? '',
    };
  };

  const cambiar = (id: string, campo: 'costo' | 'venta' | 'descripcion', valor: string) =>
    setPrecios((p) => ({ ...p, [id]: { ...de(id), [campo]: valor } }));

  /**
   * Si a **este renglón** se le pone precio de venta.
   *
   * Karin: «Si no genera costo: Se envia a operaciones a autorizar pero
   * con la diferencia de que no le ponen precio venta». Lo probó y esta
   * pantalla se lo siguió pidiendo, porque no miraba la casilla.
   *
   * Era `orden.tieneCosto`, de la orden entera, hasta la 0076. No sabía
   * contestar al caso mixto —mano de obra de iguala más una refacción que
   * sí se cobra— y ahí es donde se perdía el cobro de la pieza: la orden
   * decía «sin costo» y la venta se iba a cero en los dos renglones.
   *
   * Cuando la iguala absorbe el renglón, su venta es cero y no se
   * pregunta: no hay nada que cobrarle a Walmart, ya lo está pagando
   * dentro de su iguala. La API la fuerza a cero de todos modos; esto es
   * para no pedir un número que se va a ignorar.
   */
  const seCobra = (r: RenglonCotizacion) => !r.cubiertoPorIguala;

  // La columna de venta se enseña si alguno de los renglones de esta área
  // se cobra. Si ninguno, no existe; si alguno sí, existe para todos y en
  // los cubiertos dice «la cubre la iguala», que es más claro que una
  // celda vacía.
  const algunoSeCobra = mios.some(seCobra);

  // El costo puede ser cero —un trabajo que no se cobra aparte— y la
  // venta no: un renglón sin precio de venta llega a facturación sin nada
  // que cobrar. Salvo que ese renglón no se cobre, y entonces no se pide.
  const listo = mios.every(
    (r) => de(r.id).costo !== ''
      && (!seCobra(r) || Number(de(r.id).venta) > 0)
      // Vacía no: la columna no admite nulos y un renglón sin
      // descripción llega a facturación sin concepto que poner.
      && de(r.id).descripcion.trim().length >= 3,
  );

  async function autorizar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/ordenes/${orden.id}/cotizacion/autorizar`, {
        renglones: mios.map((r) => ({
          renglonId: r.id,
          precioCosto: Number(de(r.id).costo),
          precioVenta: seCobra(r) ? Number(de(r.id).venta) : 0,
          // Sólo si de verdad cambió: mandarla siempre escribiría en la
          // bitácora un «pasa a» idéntico en cada renglón firmado.
          descripcion: de(r.id).descripcion.trim() !== r.descripcion
            ? de(r.id).descripcion.trim()
            : undefined,
        })),
      });
      alGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <div className="panel" style={{ marginTop: 14 }}>
      <h3 style={{ marginTop: 0 }}>Te toca autorizar</h3>
      <p className="sub" style={{ marginTop: 0 }}>
        {algunoSeCobra
          ? <>Ponle precio a lo que apruebas. El costo es lo que a APRISA le
             cuesta y la venta lo que se le cobra al cliente; de las dos
             sumas salen el costo de la orden y lo que se va a facturar. El
             costo que venga escrito es el que capturó Soporte al
             registrar: se puede corregir.</>
          : <>Ponle costo a lo que apruebas: es lo que a APRISA le cuesta, y
             de ahí sale lo que se le paga a quien hizo el trabajo. <strong>
             Sin precio de venta</strong>, porque la iguala absorbe todos
             estos conceptos: no hay nada que cobrarle al cliente. El costo
             que venga escrito es el que capturó Soporte al registrar: se
             puede corregir.</>}
      </p>
      {/* Que la cotización va mezclada, dicho antes de la tabla.
          Es el caso que la 0076 vino a permitir, y es el que más se
          puede leer mal: una tabla donde unos renglones piden venta y
          otros no parece un error de la pantalla si nadie lo anuncia. */}
      {algunoSeCobra && mios.some((r) => !seCobra(r)) && (
        <div className="ayuda" style={{ marginBottom: 10 }}>
          De estos conceptos, {mios.filter(seCobra).length} se le cobra
          {mios.filter(seCobra).length === 1 ? '' : 'n'} al cliente y{' '}
          {mios.filter((r) => !seCobra(r)).length} lo absorbe la iguala. A
          los que absorbe no se les pone venta; su costo se paga igual.
        </div>
      )}

      <Error error={error} />

      <table>
        <thead>
          <tr>
            <th>Descripción</th>
            <th className="numero">Cant.</th>
            <th className="numero">Costo unitario</th>
            {algunoSeCobra && <th className="numero">Venta unitaria</th>}
            <th className="numero">{algunoSeCobra ? 'Importe' : 'Costo total'}</th>
          </tr>
        </thead>
        <tbody>
          {mios.map((r) => (
            <tr key={r.id}>
              {/* Editable: es el unico dato del renglon que no se podia
                  tocar al firmarlo, y es el que acaba en el concepto de la
                  factura. Lo que escribio Soporte sirve para entenderse
                  por dentro —«cambio de compresor»— y no siempre para que
                  el cliente lo lea en su CFDI. El codigo se queda fijo: es
                  del catalogo, no del renglon. */}
              <td>
                <span className="mono">{r.codigo ?? '—'}</span>
                <input
                  value={de(r.id).descripcion}
                  maxLength={300}
                  style={{ width: '100%', marginTop: 4 }}
                  onChange={(e) => cambiar(r.id, 'descripcion', e.target.value)}
                />
              </td>
              <td className="numero">{r.cantidad}</td>
              <td className="numero">
                <input
                  type="number" min="0" step="0.01" style={{ width: 110 }}
                  value={de(r.id).costo}
                  onChange={(e) => cambiar(r.id, 'costo', e.target.value)}
                />
              </td>
              {algunoSeCobra && (
                <td className="numero">
                  {seCobra(r) ? (
                    <input
                      type="number" min="0" step="0.01" style={{ width: 110 }}
                      value={de(r.id).venta}
                      onChange={(e) => cambiar(r.id, 'venta', e.target.value)}
                    />
                  ) : (
                    // Y no una celda vacía: vacío se lee como «falta
                    // capturarlo» y haría que alguien busque dónde
                    // escribir.
                    <span className="ayuda">La cubre la iguala</span>
                  )}
                </td>
              )}
              <td className="numero">
                {dinero(r.cantidad * (Number(seCobra(r) ? de(r.id).venta : de(r.id).costo) || 0))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!listo && (
        <Faltantes que={[algunoSeCobra
          ? 'el costo de cada renglón, y la venta de los que se le cobran al'
            + ' cliente. Esa venta no puede ser cero.'
          : 'el costo de cada renglón.']}
        />
      )}

      <div className="controles" style={{ marginTop: 12 }}>
        <button className="primario" disabled={trabajando || !listo} onClick={() => void autorizar()}>
          {trabajando ? 'Autorizando…' : `Autorizar ${mios.length} renglón(es)`}
        </button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════ los renglones de la cotización

/**
 * Un renglón mientras se captura.
 *
 * Los precios viven como texto y no como número: un campo numérico vacío
 * daría NaN, y `0` mientras se teclea impide borrar para escribir otra
 * cosa. Se convierten al mandar.
 */
interface RenglonEnCaptura {
  /** Sólo para la lista de React; no viaja a la API. */
  llave: number;
  /**
   * El renglón que ya existía, cuando se está corrigiendo la cotización.
   *
   * Viaja a la API y es lo que permite conservar la firma de lo que no se
   * tocó: sin él, cambiarle la cantidad a una pieza se leería como quitar
   * una y agregar otra, y el área tendría que autorizar de nuevo sin
   * razón. Vacío en el renglón que se acaba de agregar.
   */
  id: string;
  /** Trabajo o pieza. La pieza es la que después se le pide a almacén. */
  tipo: 'servicio' | 'refaccion';
  servicioReparacionId: string;
  /**
   * La pieza escogida, entera: es lo que el buscador recibe y devuelve.
   *
   * Nula en el renglón que llega ya guardado, donde sólo se conoce su id
   * y su texto. Por eso el id viaja aparte en `refaccionId`: reabrir la
   * cotización no puede obligar a volver a buscar cada pieza.
   */
  refaccion: Refaccion | null;
  refaccionId: string;
  codigo: string;
  descripcion: string;
  cantidad: string;
  /**
   * Lo que a APRISA le cuesta el servicio. Texto y no número, como la
   * cantidad: un campo numérico vacío daría NaN, y un `0` mientras se
   * teclea impide borrar para escribir otra cosa.
   *
   * Sólo se captura en los servicios y puede quedar vacío. En blanco
   * viaja como ausente, que dice «todavía no se sabe»; un cero diría «es
   * gratis» y se sumaría como tal.
   */
  precioCosto: string;
  /**
   * A quién se le paga este servicio. De aquí nace su vale TOT.
   *
   * Obligatorio en los servicios: `tot.proveedor_id` no admite nulos, así
   * que sin esto el vale no puede generarse solo y alguien tendría que
   * acordarse de capturarlo después. Lo sabe quien está aquí — acaba de
   * escribir lo que ese taller le dijo que cuesta.
   */
  proveedorId: string;
  /** Su nombre, para poder enseñarlo sin volver a buscarlo. */
  proveedorNombre: string;
  /**
   * Si la iguala absorbe este concepto.
   *
   * La pregunta que hasta la 0076 se contestaba una vez para toda la
   * orden. Karin: «hay ocasiones que puede no tener costo de forma
   * natural por que es full, pero el tecnico dice, si hay que traer una
   * refaccion esa si es con costo, como la registro?».
   *
   * Booleano y no texto: aquí no hay estado intermedio que proteger como
   * en los precios —la casilla llega con la propuesta puesta y se marca o
   * se desmarca—.
   */
  cubiertoPorIguala: boolean;
}

let contadorDeRenglones = 0;
function renglonVacio(tipo: 'servicio' | 'refaccion' = 'servicio'): RenglonEnCaptura {
  contadorDeRenglones += 1;
  return {
    llave: contadorDeRenglones,
    id: '',
    tipo,
    servicioReparacionId: '',
    refaccion: null,
    refaccionId: '',
    codigo: '',
    descripcion: '',
    cantidad: '1',
    precioCosto: '',
    proveedorId: '',
    proveedorNombre: '',
    // Cobrado, que es el lado que no deja de facturar por descuido. La
    // propuesta de la matriz la pone el efecto que mira a la orden.
    cubiertoPorIguala: false,
  };
}

/**
 * Un renglón ya guardado, listo para corregirlo.
 *
 * Sin esto, el formulario se abría en blanco: para agregar una refacción
 * olvidada había que volver a teclear los seis renglones que ya estaban,
 * y de paso perdían su firma. Traerlos es la mitad de poder editar; la
 * otra mitad la hace la API, que compara en vez de reemplazar.
 *
 * El precio de venta no se trae: lo pone el área al autorizar y aquí no
 * se captura. Que no viaje es además lo que hace que un renglón intacto
 * se vea como intacto.
 */
function renglonDesdeLoGuardado(r: RenglonCotizacion): RenglonEnCaptura {
  contadorDeRenglones += 1;
  return {
    llave: contadorDeRenglones,
    id: r.id,
    tipo: r.tipo,
    servicioReparacionId: r.servicioReparacionId ?? '',
    refaccion: null,
    refaccionId: r.refaccionId ?? '',
    codigo: r.codigo ?? '',
    descripcion: r.descripcion,
    cantidad: String(r.cantidad),
    precioCosto: r.precioCosto === null ? '' : String(r.precioCosto),
    // Se conserva al reabrir: sin esto, corregir una cotización obligaría
    // a volver a buscar a quién se le paga cada servicio, y el renglón
    // perdería su firma por un dato que nadie tocó.
    proveedorId: r.proveedorId ?? '',
    proveedorNombre: r.proveedor?.nombre ?? '',
    // Se conserva igual que el proveedor, y por un motivo más caro:
    // perderla al reabrir pondría precio de venta en un renglón que
    // alguien había marcado cubierto —cobrar lo que la iguala ya paga— o
    // al revés.
    cubiertoPorIguala: r.cubiertoPorIguala === true,
  };
}

/**
 * Las mismas reglas que la API, para no ofrecer un botón que va a fallar.
 *
 * De precios no se dice nada porque aquí no se capturan: los pone el área
 * que autoriza —Operaciones los servicios, Almacén las refacciones—, que
 * es quien los sabe.
 *
 * La refacción exige la pieza del catálogo. Escrita a mano no se puede
 * surtir: almacén no sabe cuál es, y la solicitud acaba en manos de
 * alguien que tiene que descifrarla.
 */
function completo(r: RenglonEnCaptura): boolean {
  return r.descripcion.trim().length >= 3
    && Number(r.cantidad) > 0
    // O la pieza que se acaba de escoger, o la que ya traía el renglón
    // guardado. Sin la segunda, reabrir la cotización dejaba en falso
    // todas las refacciones que ya estaban bien.
    && (r.tipo === 'servicio' || r.refaccion !== null || r.refaccionId !== '')
    // A quién se le paga el servicio. Se comprueba aquí con la misma
    // regla que la API para que el botón no prometa algo que va a
    // fallar: de esto nace el vale TOT y sin proveedor no se genera.
    && (r.tipo === 'refaccion' || r.proveedorId !== '');
}


// ═════════════════════════════════════ la bandeja de quien autoriza

/**
 * Las cotizaciones que están esperando la firma de quien mira.
 *
 * No es una bandeja sino dos, y por eso el componente es el mismo: la
 * lista la filtra la API según el permiso de firma, así que a Operaciones
 * le salen las órdenes con servicios pendientes y a Almacén las que
 * tienen refacciones pendientes. Una orden con las dos cosas sale en las
 * dos, y se le cae a cada área en cuanto firma lo suyo.
 *
 * Antes de esto sólo existía la de Operaciones, y le llegaban también las
 * refacciones —que no le tocan— mientras Almacén no tenía dónde ver las
 * suyas: podía firmarlas desde la ficha, pero sin lista no había cómo
 * saber que había algo esperándolo.
 *
 * Se abre la misma ficha que usa la pantalla de órdenes. Duplicar la
 * vista para «la versión de quien autoriza» acabaría enseñando una orden
 * distinta de la que capturó Soporte operativo, que es justo lo que no
 * puede pasar cuando lo que se decide es si el trabajo procede.
 */
export function CotizacionesPorAutorizar() {
  const { puede } = useSesion();
  const [pagina, setPagina] = useState<Pagina<Orden> | null>(null);
  const [numero, setNumero] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [tecnicos, setTecnicos] = useState<Opcion[]>([]);

  const mias = areasQueFirma(puede);

  useEffect(() => {
    api.get<Opcion[]>('/tecnicos/opciones').then(setTecnicos).catch(() => undefined);
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Orden>>('/ordenes', {
      bandeja: 'por_autorizar', pagina: numero, limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [numero]);

  useEffect(cargar, [cargar]);

  /** Lo que le falta a esta orden de las áreas que uno firma, y sólo eso. */
  const loMio = (o: Orden) => o.cotizacionPendiente.filter((p) => mias.includes(p.area));

  return (
    <div className="panel">
      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">No hay cotizaciones esperando tu autorización.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Sucursal</th>
                  <th>Falla</th>
                  <th>Técnico</th>
                  <th>Solicitó</th>
                  {/* Cuánto le toca a uno, sin tener que abrir la orden
                      para averiguarlo. En una lista larga es la
                      diferencia entre priorizar y abrirlas todas. */}
                  <th>Te toca</th>
                  <th>Prioridad</th>
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((o) => (
                  <tr key={o.id}>
                    <td className="mono">
                      <button className="enlace" onClick={() => setAbierta(o.id)}>
                        {folioOT(o.folio)}
                      </button>
                    </td>
                    <td>
                      {o.sucursal
                        ? `${o.sucursal.determinante} — ${o.sucursal.nombre}`
                        : '—'}
                    </td>
                    <td style={{ maxWidth: 280 }}>{o.descripcionFalla}</td>
                    <td>{o.tecnico?.nombre_completo ?? '—'}</td>
                    <td>{o.responsable?.nombre ?? '—'}</td>
                    <td>
                      {loMio(o).map((p) => (
                        <div key={p.area}>
                          {p.renglones}{' '}
                          {p.area === 'operaciones'
                            ? (p.renglones === 1 ? 'servicio' : 'servicios')
                            : (p.renglones === 1 ? 'refacción' : 'refacciones')}
                        </div>
                      ))}
                      {loMio(o).length === 0 && '—'}
                    </td>
                    <td><Prioridad valor={o.prioridad} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {abierta && (
        /**
         * Aquí no va `onIrALaVisita` a propósito: a esta bandeja llegan
         * cotizaciones por firmar, y una preventiva no trae cotización que
         * firmar. Ofrecer el viaje al programa sería un botón que nunca
         * aparece, y uno que no se puede probar es uno que se rompe.
         */
        <Ficha
          id={abierta}
          tecnicos={tecnicos}
          onCerrar={() => setAbierta(null)}
          onCambio={cargar}
        />
      )}
    </div>
  );
}


/**
 * Los vales de taller externo de una orden.
 *
 * El TOT vive aquí porque de una orden cuelga siempre. Antes se creaba
 * desde su propia pantalla buscando la orden en un buscador, que es el
 * paso al revés: obligaba a acordarse del folio y dejaba abierta la
 * puerta a colgar el gasto de la orden equivocada.
 *
 * Cada vale creado se va a la bandeja de Operaciones. Cuando lo autoriza
 * nace la solicitud de pago para Contabilidad, ya con su firma.
 */
function ValesDeLaOrden({ orden, puede }: {
  orden: OrdenDetalle;
  puede: (m: string, a: string) => boolean;
}) {
  const [vales, setVales] = useState<Tot[] | null>(null);
  const [creando, setCreando] = useState(false);
  const [abierto, setAbierto] = useState<Tot | null>(null);

  const cargar = useCallback(() => {
    api.get<Pagina<Tot>>('/tot', { ordenTrabajoId: orden.id, limite: 50 })
      .then((p) => setVales(p.data))
      .catch(() => setVales([]));
  }, [orden.id]);

  useEffect(cargar, [cargar]);

  /**
   * Una orden cerrada o cancelada ya no admite vales nuevos.
   *
   * Karin: «en las ordenes cerradas, sigue activa la opcion de "crear
   * TOT" y ya no debe de salir». Y no es sólo que sobre: cerrar manda la
   * orden a facturación, así que un vale nacido después es un gasto que
   * llega cuando el precio al cliente ya está fijado y la factura ya se
   * armó. Ese costo no se recupera de nadie.
   *
   * Los vales que ya existen se siguen viendo: la orden cerrada es
   * justamente donde alguien va a consultar qué se pagó.
   */
  const cerrada = ['cierre_admin', 'cancelado'].includes(orden.estatus);
  const sePuedeCrear = puede('tot', 'crear') && !cerrada;

  // Sin vales y sin poder crearlos, la sección no aporta nada.
  if (!puede('tot', 'ver')) return null;
  if (vales !== null && vales.length === 0 && !sePuedeCrear) return null;

  return (
    <div className="seccion">
      <h2>Trabajos en otros talleres</h2>

      {vales === null ? <Cargando /> : vales.length === 0 ? (
        <p className="sub">Esta orden no tiene vales.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Folio</th>
              <th>Taller</th>
              <th className="numero">Total</th>
              <th>Estatus</th>
            </tr>
          </thead>
          <tbody>
            {vales.map((v) => (
              <tr key={v.id}>
                <td className="mono">
                  <button className="enlace" onClick={() => setAbierto(v)}>{v.folio}</button>
                </td>
                <td>{v.proveedor?.nombre ?? '—'}</td>
                <td className="numero">{dineroDelVale(v.total)}</td>
                <td><EtiquetaEstatusTot valor={v.estatus} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {sePuedeCrear && (
        <div className="controles" style={{ marginTop: 10 }}>
          <button onClick={() => setCreando(true)}>Crear TOT</button>
        </div>
      )}

      {creando && (
        <FormularioTot
          ordenFija={orden}
          onCerrar={() => setCreando(false)}
          onGuardado={() => { setCreando(false); cargar(); }}
        />
      )}

      {abierto && (
        <FichaTot
          tot={abierto}
          puede={puede}
          onCerrar={() => { setAbierto(null); cargar(); }}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════ ficha

/**
 * La ficha completa de una orden.
 *
 * Se exporta porque la usa también la bandeja de Operaciones: quien
 * autoriza necesita ver exactamente lo mismo que ve quien capturó, y dos
 * fichas distintas para la misma orden acabarían enseñando cosas
 * distintas. Los botones los dicta la API, así que cada perfil ve los
 * suyos sin que la pantalla decida nada.
 */
const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/**
 * El lado preventivo de la orden.
 *
 * Marcar la visita como realizada en el programa **no cierra esta
 * orden**: cerrar pide resultado y descripción del trabajo, que el acuse
 * de la visita no tiene, y hacerlo por detrás dejaría una orden en
 * facturación sin que nadie escribiera qué se hizo.
 *
 * Lo que sí hacía falta era verlo desde aquí. Antes la orden se quedaba
 * abierta —y con el tiempo vencida— mientras el programa la daba por
 * cumplida, y las dos pantallas no se enteraban la una de la otra.
 */
/**
 * El lado preventivo de la orden, y la puerta a donde se gestiona.
 *
 * Karin: «lo que yo creo que podemos hacer es quitar de "ordenes" las
 * preventivas […] en "preventivo" y desde ahi gestionamos esas […] ya no
 * tenemos que estar haciendolo en 2 secciones diferentes».
 *
 * Tenía razón en el diagnóstico: los tres callejones de un mismo día
 * —la visita acusada sin servicios, la orden sin cotización, el botón de
 * cerrar apagado por lo que se captura dentro— salieron todos de que una
 * preventiva se operaba desde dos sitios con reglas distintas, y cada
 * uno daba por hecho algo del otro.
 *
 * Lo que **no** se hace es sacarla de Órdenes. Una preventiva es una
 * orden: tiene evidencias, bitácora, refacciones, PRE-PO y vales, y la
 * bandeja es donde se ve qué está atorado. Lo que sobra no es la orden,
 * son los pasos repetidos.
 *
 * Así que Preventivo manda sobre el flujo y Órdenes se queda de
 * consulta: aquí no se cierra ni se rechaza, se va a la visita.
 */
function ElPreventivo({ datos, onIrALaVisita }: {
  datos: PreventivoDeLaOrden;
  onIrALaVisita?: (anio: number, mes: number, visita?: string) => void;
}) {
  const visita = datos.visita;

  return (
    <div className="aviso">
      <strong>Esta orden es la de una visita preventiva.</strong>

      {visita && (
        <p style={{ margin: '6px 0 0' }}>
          Programa de {MESES[visita.mes - 1]} de {visita.anio}
          {visita.tecnico ? ` · ${visita.tecnico.nombre_completo}` : ''}
          {visita.noOs ? ` · OS ${visita.noOs}` : ''}
          {visita.estatus === 'realizada'
            ? ` · acusada el ${visita.realizadaEn ? fechaCorta(visita.realizadaEn) : '—'}`
            : ' · todavía sin acusar en el programa'}
          .
          {visita.estatus === 'realizada'
            ? ' El programa ya la da por cumplida; desde ahí se cierra y se'
              + ' manda a facturación.'
            : ''}
        </p>
      )}

      {/*
        La puerta, no un atajo.

        El flujo de una preventiva vive en el programa: ahí se acusa, se
        eligen los servicios, se sube la OS firmada y se cierra. Desde
        aquí sólo se va. Tener los dos botones es lo que produjo los
        callejones.
      */}
      {visita && onIrALaVisita && (
        <div className="controles" style={{ marginTop: 8 }}>
          <button
            className="primario"
            onClick={() => onIrALaVisita(visita.anio, visita.mes, visita.id)}
          >
            Ir a la visita de {MESES[visita.mes - 1]} {visita.anio}
          </button>
        </div>
      )}

      {/*
        Cómo se juntaron esta orden y su visita.

        Se dice porque es lo que soporte hacía a mano: el técnico se
        adelantaba, mandaba su OS, y alguien buscaba la OT cuando llegaba
        para relacionarlas. Ahora lo hace la carga por sucursal y mes, y
        quien lea la orden tiene derecho a saber que ese emparejamiento no
        lo decidió una persona.
      */}
      {visita?.enlaceAutomatico && (
        <p className="ayuda" style={{ margin: '6px 0 0' }}>
          La carga emparejó esta orden con la visita por sucursal y mes.
          Si no es la que corresponde, se puede deshacer desde el programa
          preventivo.
        </p>
      )}

      {datos.servicios.length > 0 && (
        <table style={{ marginTop: 8 }}>
          <tbody>
            {datos.servicios.map((s) => (
              <tr key={s.id}>
                <td className="mono">{s.codigo}</td>
                <td>
                  {s.nombre}
                  {/* Los equipos que se atendieron con ese servicio. Sin
                      importe: el precio es del renglón y no se multiplica
                      —«todo eso ya tiene un costo pactado»—. */}
                  {s.equipos.length > 0 && (
                    <div className="ayuda" style={{ marginTop: 2 }}>
                      Equipos: {s.equipos.map((e) => e.nombre).join(' · ')}
                    </div>
                  )}
                </td>
                <td className="numero">
                  {s.precio !== null
                    ? dinero(s.precio)
                    : <span className="etiqueta no">sin precio</span>}
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={2}><strong>Total</strong></td>
              <td className="numero">
                <strong>
                  {datos.total !== null ? dinero(datos.total) : 'no se puede sumar'}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

export function Ficha({
  id, tecnicos, onCerrar, onCambio, onIrALaVisita,
}: {
  id: string;
  tecnicos: Opcion[];
  onCerrar: () => void;
  onCambio: () => void;
  /**
   * A dónde se va para gestionar una preventiva.
   *
   * Sin esto —y es el caso cuando la ficha ya se abrió **desde** el
   * programa— no se ofrece el viaje: ya estás ahí. Preventivo la pinta
   * sin la prop y el botón no sale.
   */
  onIrALaVisita?: (anio: number, mes: number, visita?: string) => void;
}) {
  const { puede } = useSesion();
  const [orden, setOrden] = useState<OrdenDetalle | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [accion, setAccion] = useState<AccionOrden | null>(null);
  const [nota, setNota] = useState('');
  const [recargaRefacciones, setRecargaRefacciones] = useState(0);
  // Los técnicos de la zona de esta orden. La lista general trae los 32
  // del país, y mandar a uno de Península a Jalisco no es una opción que
  // deba estar sobre la mesa.
  const [tecnicosDeLaZona, setTecnicosDeLaZona] = useState<Opcion[] | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const archivoRef = useRef<HTMLInputElement>(null);
  const [corrigiendo, setCorrigiendo] = useState(false);
  /** Reiniciar la orden. Sólo gerencia, y sólo antes de la línea. */
  const [reiniciando, setReiniciando] = useState(false);

  const cargar = useCallback(() => {
    api.get<OrdenDetalle>(`/ordenes/${id}`).then(setOrden).catch(setError);
  }, [id]);

  useEffect(cargar, [cargar]);

  useEffect(() => {
    if (!orden?.region?.id) { setTecnicosDeLaZona(null); return; }
    api.get<Opcion[]>('/tecnicos/opciones', { regionId: orden.region.id })
      .then(setTecnicosDeLaZona)
      .catch(() => setTecnicosDeLaZona(null));
  }, [orden?.region?.id]);

  async function agregarNota() {
    if (nota.trim().length < 5) return;
    setTrabajando(true);
    try {
      await api.post(`/ordenes/${id}/seguimiento`, {
        tipo: 'nota_informativa',
        descripcion: nota.trim(),
      });
      setNota('');
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  async function subirEvidencia(archivo: File, tipo: 'foto' | 'pre_po' = 'foto') {
    setError(null);
    setTrabajando(true);
    try {
      // FormData y no JSON: el archivo viaja binario, sin inflarse un
      // tercio como haría en base64.
      //
      // Y encogida antes de salir: una foto de teléfono pesa diez megas
      // y el técnico la sube desde dentro de una bodega. Lo que se gana
      // no es disco, es que la subida termine. Ver `imagenes.ts`.
      const cuerpo = new FormData();
      cuerpo.append('archivo', await aligerar(archivo));

      const respuesta = await fetchAutenticado(`/api/v1/ordenes/${id}/evidencias?tipo=${tipo}`, {
        method: 'POST',
        body: cuerpo,
      });

      if (!respuesta.ok) {
        const cuerpoError = await respuesta.json().catch(() => ({}));
        throw new ErrorApi(
          respuesta.status,
          String(cuerpoError.codigo_negocio ?? 'error'),
          String(cuerpoError.detail ?? 'No se pudo subir la evidencia.'),
        );
      }
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
      if (archivoRef.current) archivoRef.current.value = '';
    }
  }

  async function quitarEvidencia(evidenciaId: string) {
    setError(null);
    try {
      await api.borrar(`/ordenes/${id}/evidencias/${evidenciaId}`);
      cargar();
    } catch (e) {
      setError(e);
    }
  }

  if (!orden) {
    return (
      <Dialogo titulo="Orden" onCerrar={onCerrar} pie={<button onClick={onCerrar}>Cerrar</button>}>
        <Error error={error} />
        {!error && <Cargando />}
      </Dialogo>
    );
  }

  /**
   * Si el flujo de esta orden lo manda el programa preventivo.
   *
   * Con visita, y no por ser preventiva a secas: una preventiva que
   * llegó sin visita del programa no tiene a dónde mandar a nadie, y
   * esconderle los botones la dejaría sin salida. Es el mismo error que
   * ya se cometió tres veces hoy, así que la condición se escribe sobre
   * lo que de verdad importa —que exista el otro lado— y no sobre el
   * tipo.
   */
  const elProgramaLaManda = Boolean(orden.preventivo?.visita);

  /**
   * La preventiva que se quedó sin visita.
   *
   * Karin, con la OT-1601758180: «me sigue apareciendo el boton de
   * cerrar orden». Y sí: es preventiva, pero la carga nunca la emparejó
   * con ninguna visita del programa, así que `elProgramaLaManda` da
   * falso y los botones se quedan.
   *
   * Dejarlos es lo correcto —sin visita no hay a dónde mandarla, y
   * esconderlos la dejaría sin salida— pero dejarlos *callados* es lo
   * que hace que parezca que el cambio no sirvió. Así que se dice: la
   * orden está suelta, y se ofrece la puerta al mes que le toca para
   * enlazarla ahí.
   *
   * Se mira el tipo y no el dato de abajo: cuando no hay ni visita ni
   * servicios, `orden.preventivo` viene nulo entero y el módulo
   * preventivo ni se pinta. Ése es justo el caso que hay que señalar.
   */
  const preventivaSuelta = orden.tipo === 'preventivo' && !orden.preventivo?.visita;

  /** El mes del programa al que pertenece: el de su fecha límite. */
  const suMes = ((): { anio: number; mes: number } => {
    const d = new Date(orden.fechaLimite ?? orden.creadoEn);
    return { anio: d.getFullYear(), mes: d.getMonth() + 1 };
  })();

  return (
    <>
      <Dialogo
        titulo={`${folioOT(orden.folio)} · ${ETIQUETAS_ESTATUS[orden.estatus] ?? orden.estatus}`}
        onCerrar={onCerrar}
        // La ficha de la orden es la más cargada del sistema: datos,
        // refacciones, evidencia y bitácora. A 780 píxeles todo eso se
        // lee en una columna larguísima.
        ancho
        /* Corregir no es avanzar: no mueve el estatus, arregla lo que se
           capturó mal. Una carga masiva trae determinantes cruzados y
           folios mal tecleados, y sin esto la única salida era cancelar y
           recapturar, perdiendo la bitácora y las refacciones que ya
           cuelgan de la orden.

           Va en la esquina del encabezado y no al final: estaba debajo de
           las acciones del flujo, y la ficha de la orden tiene arriba de
           ella los datos, el equipo, la cotización, las refacciones y los
           avisos de cierre. Quien entra a arreglar un folio mal escrito
           no tiene por qué recorrer todo eso, y el encabezado está fijo,
           así que de ahí no se va. Sin destacar y separado de las
           acciones por lo de siempre: no es un paso del flujo, y
           mezclarlo con ellas lo hacía parecer uno. */
        acciones={puede('ordenes', 'editar')
          && !['cierre_admin', 'cancelado'].includes(orden.estatus)
          ? <button onClick={() => setCorrigiendo(true)}>Corregir datos</button>
          : null}
        pie={<button onClick={onCerrar}>Cerrar</button>}
      >
        <Error error={error} />

        {orden.vencida && (
          <div className="aviso error">
            Vencida: la fecha límite era {soloDia(orden.fechaLimite as string)}.
          </div>
        )}

        {/* De quién está esperando, arriba y antes de todo lo demás.
            Karin: «el estatus de la orden sigue siendo en espera de
            refacciones, ¿que podemos hacer para que se sepa exactamente
            de quien esta dependiendo esa orden?».

            El título del diálogo dice el estatus, y ése es justo el que
            no distingue nueve situaciones con dueños distintos. Así que
            la aclaración va pegada a él.

            Es un aviso y no una línea de datos porque es lo que alguien
            viene a buscar al abrir una orden atorada: quién la tiene.
            Qué le falta completo lo dice «No se puede cerrar todavía»,
            más abajo, que es otra pregunta. */}
        {orden.dependeDe && (
          <div className="aviso">
            <strong>Esperando a {orden.dependeDe.area}:</strong>
            {' '}
            {orden.dependeDe.falta}
            {' '}
            <span className="mono">({orden.dependeDe.folio})</span>
          </div>
        )}

        {/*
          El orden de las secciones, que es una decisión y no una casualidad.

          Karin lo pidió así, y la lógica que sigue es la de quien abre la
          orden: primero qué es, después qué hacer con ella, y al final lo
          que se le va colgando.

            1. Los avisos  · de quién depende, si está vencida
            2. Datos       · qué orden es
            3. Cotización  · qué se va a hacer y qué cuesta
            4. Cierre      · qué se hizo, cuando ya está cerrada
            5. ¿Qué sigue? · el paso, con «Volver a empezar» dentro
            6. Refacciones · lo que la orden necesita
            7. Talleres    · lo que se le paga a terceros
            8. PRE-PO      · el papel del cliente
            9. Evidencia   · las fotos y la OS firmada
           10. Bitácora    · lo que pasó

          Lo que cambió respecto de antes: «¿Qué sigue?» estaba por debajo
          de los vales y del cierre, así que la acción —lo que de verdad se
          viene a hacer— quedaba sepultada bajo tres bloques de consulta. Y
          «Volver a empezar» vivía en su propia sección entre medias, que es
          donde nadie la encontraba.
        */}

        {/* Los identificadores en columnas: son etiquetas de dos palabras
            y uno por renglón dejaba media pantalla vacía obligando a
            bajar. Lo que se lee como prosa —la falla, el trabajo hecho—
            se queda con la fila entera. */}
        <div className="rejilla-datos">
          <Dato etiqueta="Sucursal" valor={
            orden.sucursal ? `${orden.sucursal.determinante} — ${orden.sucursal.nombre}` : null
          } />
          <Dato etiqueta="Región" valor={orden.region?.nombre ?? null} />
          <Dato etiqueta="Estado" valor={orden.estado?.nombre ?? null} />
          <Dato etiqueta="Responsable" valor={orden.responsable?.nombre ?? null} />
          <Dato etiqueta="Técnico" valor={orden.tecnico?.nombre_completo ?? null} />
          {/* La iguala la define la sucursal y aquí no se cambia; se enseña
              porque es lo que propone si el trabajo lleva cargo aparte
              cuando nadie ha decidido nada en la orden. */}
          <Dato etiqueta="Iguala" valor={orden.tipoIguala?.nombre ?? null} />
          <Dato etiqueta="Tipo y prioridad" valor={`${orden.tipo} · ${orden.prioridad}`} />
          {/* La decisión que más dinero mueve, y no se veía por ningún
              lado. Karin: «cuando entras a la orden, no veo en ningun
              lugar que si tiene costo».

              Desde la 0076 ya no es una respuesta sino una cuenta: sale
              de la casilla «Lo cubre la iguala» de cada renglón. Pero la
              cuenta entera no cabe en una rejilla de datos —«Esta orden
              sí genera costo: se le cobra 1 de 2 conceptos…» es un
              párrafo, no un dato— y leída así deja de contestar la
              pregunta de un vistazo. Karin, volviendo sobre lo mismo:
              «podemos agregar si tiene o no costo? como dato
              informativo?».

              Así que el valor es Sí o No, que es lo que se viene a saber,
              y el porqué va debajo en gris para quien pregunte. */}
          <div className="ancho-total">
            <Dato
              etiqueta="¿Se le cobra algo al cliente?"
              valor={orden.tieneCosto ? 'Sí' : 'No'}
              detalle={orden.cotizacion.length > 0
                ? orden.comoQuedoElCosto
                : orden.porQueTieneCosto}
            />
          </div>
          {/* El equipo son cuatro datos, no uno. Enseñar sólo el tipo
              obliga a abrir la edición para saber de qué marca y modelo
              se está hablando, que es lo primero que pregunta el técnico
              cuando le mandan la orden. */}
          <Dato etiqueta="Área" valor={orden.area?.nombre ?? null} />
          <Dato etiqueta="Equipo" valor={
            orden.tipoEquipo?.nombre ?? orden.equipoTexto ?? null
          } />
          <Dato etiqueta="Marca" valor={orden.marca?.nombre ?? null} />
          <Dato etiqueta="Modelo" valor={orden.modelo?.nombre ?? null} />

          <div className="ancho-total">
            <Dato etiqueta="Falla reportada" valor={orden.descripcionFalla} />
          </div>
          {orden.notasTecnico && (
            <div className="ancho-total">
              <Dato etiqueta="Notas del técnico" valor={orden.notasTecnico} />
            </div>
          )}
        </div>

        {/* Azul: lo que se le cobra al cliente. Ver `.seccion` en los
            estilos, donde está el código de color completo. */}
        {orden.cotizacion.length > 0 && (
          <div className="seccion acento">
            <h2>Cotización del técnico</h2>
            <table>
              <thead>
                <tr>
                  <th>Qué es</th>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th className="numero">Cant.</th>
                  <th>Autoriza</th>
                  <th className="numero">Costo</th>
                  <th className="numero">Venta</th>
                </tr>
              </thead>
              <tbody>
                {orden.cotizacion.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.tipo === 'refaccion' ? (
                        <span className={r.enAlmacen ? 'etiqueta si' : 'etiqueta'}>
                          {r.enAlmacen ? 'Pedida a almacén' : 'Refacción'}
                        </span>
                      ) : 'Trabajo'}
                    </td>
                    <td className="mono">{r.codigo ?? '—'}</td>
                    <td>
                      {r.descripcion}
                      {/* A quién se le paga. Va debajo de la descripción
                          porque es de ese renglón: de aquí nace su vale
                          TOT, y quien mira la cotización tiene que poder
                          ver a quién se le va a pagar sin abrir nada. */}
                      {r.proveedor && (
                        <div className="ayuda">Se le paga a {r.proveedor.nombre}</div>
                      )}
                    </td>
                    <td className="numero">{r.cantidad}</td>
                    {/* Quién debe firmarlo, y si ya lo firmó. Sin esto,
                        una cotización a medias se ve igual que una
                        completa con precios en cero. */}
                    <td>
                      <span className={r.autorizadoEn ? 'etiqueta si' : 'etiqueta'}>
                        {NOMBRE_DEL_AREA[r.area]}
                        {r.autorizadoEn ? ' ✓' : ' · pendiente'}
                      </span>
                    </td>
                    <td className="numero">
                      {r.precioCosto === null
                        ? <span className="ayuda">sin cotizar</span>
                        : dinero(r.cantidad * r.precioCosto)}
                    </td>
                    <td className="numero">
                      {/* Cubierto se dice, no se enseña como cero.
                          Un cero y un «sin cotizar» se leen igual de mal
                          aquí: el primero parece un error de captura y el
                          segundo parece que falta una firma. Lo que pasa
                          es que ese concepto no se le cobra, y eso es un
                          hecho, no un hueco. */}
                      {r.cubiertoPorIguala
                        ? <span className="ayuda">la cubre la iguala</span>
                        : r.precioVenta === null
                          ? <span className="ayuda">sin cotizar</span>
                          : dinero(r.cantidad * r.precioVenta)}
                    </td>
                  </tr>
                ))}
                <tr>
                  {/* Parcial mientras falte alguna firma: es la suma de
                      lo cotizado hasta ahora, no el total del trabajo. */}
                  <td colSpan={5}>
                    <strong>
                      {orden.cotizacionPendiente.length > 0 ? 'Total parcial' : 'Total'}
                    </strong>
                  </td>
                  <td className="numero">
                    <strong>
                      {dinero(orden.cotizacion.reduce(
                        (s, r) => s + r.cantidad * (r.precioCosto ?? 0), 0,
                      ))}
                    </strong>
                  </td>
                  <td className="numero">
                    <strong>
                      {dinero(orden.cotizacion.reduce(
                        (s, r) => s + r.cantidad * (r.precioVenta ?? 0), 0,
                      ))}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Lo que a este perfil le toca firmar. Va aquí y no en una
                pantalla aparte porque la decisión se toma mirando la
                orden: qué equipo es, qué falla tiene y qué pidió el
                técnico. Sacarlo a una bandeja suelta obligaría a abrir la
                orden en otra pestaña para poder decidir. */}
            <PorAutorizar
              orden={orden} puede={puede}
              alGuardar={() => { cargar(); onCambio(); }}
            />
          </div>
        )}

        {orden.fechaCierre && (
          <div className="seccion">
            <h2>Cierre</h2>
            <div className="rejilla-datos">
              <Dato etiqueta="Resultado" valor={orden.resultado} />
              <Dato etiqueta="Firma" valor={orden.firma} />
              <Dato etiqueta="Cerrada" valor={fechaCorta(orden.fechaCierre)} />
              <Dato etiqueta="No. de orden de servicio" valor={orden.noOsCliente} />
              <div className="ancho-total">
                <Dato etiqueta="Trabajo realizado" valor={orden.descripcionTrabajo} />
              </div>
            </div>
          </div>
        )}

        {/* A dónde puede pasar la orden desde donde está. Las dicta la
            API según el estado y el perfil, no la pantalla.

            Antes salían las cinco del mismo azul y con la explicación
            escondida en el tooltip: cuatro caminos compitiendo por la
            vista y ninguno diciendo cuál es el normal. Ahora se destaca
            uno solo —el paso esperado— y cada uno lleva debajo por qué
            existe. */}
        {(orden.acciones.length > 0 || puede('ordenes', 'editar') || orden.reinicio) && (
          <div className="seccion">
            <h2>
              ¿Qué sigue?{' '}
              <span className="sub" style={{ fontWeight: 400 }}>
                la orden está en <QueEspera orden={orden} />
              </span>
            </h2>

            {/* Lo que impide cerrarla, dicho antes de intentarlo.
                La API se niega igual, pero el botón de cerrar sigue ahí
                —cancelar la orden también está en esa lista y ése sí se
                puede— así que la explicación va arriba, a la vista. */}
            {/* Qué áreas deben todavía su firma. Sin esto, soporte ve el
                botón de confirmar apagado y no sabe a quién ir a buscar. */}
            {orden.cotizacionPendiente.length > 0 && (
              <div className="aviso">
                <strong>
                  Esta cotización espera las firmas de las áreas.
                </strong>
                {/* Dónde se firma, porque no se firma aquí. Era la queja
                    de fondo: el estatus pedía una autorización y esta
                    pantalla no tiene dónde darla. */}
                <p style={{ margin: '6px 0 0' }}>
                  Cada área firma lo suyo desde su propia bandeja, y de paso
                  le pone precio. Aquí sólo se confirma cuando ya no falta
                  ninguna.
                </p>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  {orden.cotizacionPendiente.map((p) => (
                    <li key={p.area}>
                      <strong>{NOMBRE_DEL_AREA[p.area]}</strong>: firmó{' '}
                      {p.total - p.renglones} de {p.total}{' '}
                      {p.total === 1 ? 'renglón' : 'renglones'}
                      {/* El conteo, no sólo lo que falta: «0 de 4» dice que
                          nadie la ha mirado y «3 de 4» que está por salir, y
                          a una se le llama y a la otra se la espera. */}
                      {p.total - p.renglones === 0 && ' (todavía no la ha mirado)'}
                      .
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {orden.pendientesDeCierre.length > 0 && (
              <div className="aviso">
                <strong>No se puede cerrar todavía.</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  {orden.pendientesDeCierre.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </div>
            )}

            {/* Lo que se le paga al taller y no se le cobra al cliente.
                Karin: «me quedo la duda que si desde un paso anterior
                marque como "si tiene costo" se entiende que se cobra al
                cliente». No: el sí/no abre la puerta, la cotización es lo
                que pasa por ella, y un concepto del vale que nunca llegó
                a la cotización se paga y no se cobra. El costo vive en el
                vale y la venta en la cotización, así que nadie cruza las
                dos cifras hasta el cierre de mes —cuando ya nadie
                recuerda de qué orden era—. Aquí se cruzan.

                Avisa y no impide: a veces de verdad no se cobra. Pero el
                cierre no pasa sin confirmarlo, y eso se decide mirando la
                cifra en vez de al final por inercia. */}
            {orden.noSeCobra.conceptos.length > 0 && (
              <div className="aviso">
                <strong>Se paga y no se cobra.</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                  {orden.noSeCobra.conceptos.map((c) => (
                    <li key={`${c.vale}-${c.descripcion}`}>
                      {c.vale}: «{c.descripcion}» por {dinero(c.importe)}
                    </li>
                  ))}
                </ul>
                <p style={{ margin: '6px 0 0' }}>{orden.noSeCobra.queHacer}</p>
              </div>
            )}

            {orden.preventivo && (
              <ElPreventivo datos={orden.preventivo} onIrALaVisita={onIrALaVisita} />
            )}

            {/*
              Una preventiva con visita no ofrece sus pasos aquí.

              Karin: «ya no tenemos que estar haciendolo en 2 secciones
              diferentes». El flujo lo manda el programa; aquí queda la
              orden para consultarla y para lo que es suyo y no de la
              visita —evidencias, bitácora, refacciones, PRE-PO, vales—.

              Con visita, y no por ser preventiva a secas: una preventiva
              que llegó sin visita del programa no tiene a dónde mandar a
              nadie, y esconderle los botones la dejaría sin salida. Es
              el mismo error que ya cometimos tres veces hoy.
            */}
            {/*
              Y si es preventiva pero nadie la enlazó, se dice por qué
              siguen aquí los botones.
            */}
            {preventivaSuelta && (
              <div className="aviso">
                <strong>
                  Esta orden es preventiva y no está enlazada a ninguna
                  visita del programa.
                </strong>
                <p style={{ margin: '6px 0 0' }}>
                  Las preventivas se gestionan en el programa: ahí se acusa la
                  visita, se eligen los servicios que se hicieron, se sube la
                  OS firmada y se cierra. Ésta no aparece enlazada, así que los
                  pasos se quedan aquí abajo para no dejarla sin salida —pero
                  lo que corresponde es enlazarla en el programa de su mes.
                </p>
                {onIrALaVisita && (
                  <div className="controles" style={{ marginTop: 8 }}>
                    <button
                      className="primario"
                      onClick={() => onIrALaVisita(suMes.anio, suMes.mes)}
                    >
                      Buscarla en el preventivo de {MESES[suMes.mes - 1]} {suMes.anio}
                    </button>
                  </div>
                )}
              </div>
            )}

            {!elProgramaLaManda && (
            <div className="acciones-flujo">
              {orden.acciones.map((a, i) => {
                // El cierre queda a la vista pero apagado mientras la
                // orden dependa de alguien. Esconderlo dejaría a quien
                // busca cerrar preguntándose dónde está el botón.
                /**
                 * Qué apaga este botón.
                 *
                 * Sólo lo que no se arregla aquí dentro. Karin, con la
                 * OT-1601760030: «no veo en las OT donde poner los
                 * servicios de preventiva» — el botón estaba apagado
                 * porque faltaban los servicios, y los servicios se
                 * capturan dentro de este mismo diálogo. Un candado con
                 * la llave del otro lado de la puerta.
                 *
                 * La lista completa se sigue enseñando arriba, en «No se
                 * puede cerrar todavía». Lo que cambia es cuál frena.
                 */
                const frenado = (a.a === 'cierre_admin'
                  && (orden.frenanElCierre ?? orden.pendientesDeCierre).length > 0)
                  // Confirmar la cotización, mientras falte una firma.
                  || (a.a === 'autorizado' && orden.estatus === 'espera_autorizacion'
                    && orden.cotizacionPendiente.length > 0);

                return (
                  <button
                    key={a.a}
                    className={a.a === 'cancelado' ? 'peligro' : (i === 0 ? 'primario' : '')}
                    disabled={frenado}
                    onClick={() => setAccion(a)}
                  >
                    <span>{a.etiqueta}</span>
                    <span className="ayuda">
                      {/* Y por qué está apagado, con el motivo real.
                          Decía siempre «falta que almacén o compras
                          terminen» porque era lo único que podía
                          frenarlo cuando se escribió; con el tiempo
                          frenaron otras cosas y el texto se quedó
                          mintiendo. Ahora sale el primero de la lista,
                          que es el que hay que destrabar. */}
                      {!frenado ? a.explicacion
                        : a.a === 'autorizado'
                          ? 'Falta que Operaciones o Almacén autoricen lo suyo.'
                          : (orden.frenanElCierre ?? orden.pendientesDeCierre)[0]
                            ?? 'Falta que almacén o compras terminen.'}
                    </span>
                  </button>
                );
              })}
            </div>
            )}

            {/*
              Volver a empezar, dentro de ¿Qué sigue? y separado de los demás.

              Karin: «Volver a empezar trata de incluirlo a nivel de ¿Qué
              sigue?». Tiene razón en que es un paso del flujo y no un
              apartado: quien busca qué hacer con la orden mira aquí, y
              tenerlo tres secciones más abajo lo escondía tanto como
              tenerlo dentro del bloque equivocado —que fue el problema de
              antes: «no encontre el boton de volver a empezar que dices»—.

              Va separado por una línea y no dentro de la rejilla, y ésa es
              la parte que no se puede perder: se parece demasiado a
              «Cancelar la orden», que acaba de salir ahí arriba y es el
              último de la fila. Dos botones rojos pegados que hacen cosas
              opuestas —cancelar mata la orden, reiniciar la deja viva— es
              exactamente como se pulsa el que no era.

              Por eso además no lleva la clase `peligro`: el rojo se queda
              para cancelar, que es el irreversible.

              Su permiso es `reiniciar_ordenes` y nada más. Si la API mandó
              el dictamen, esto se pinta, aunque no haya ninguna otra acción
              disponible.
            */}
            {orden.reinicio && (
              <div style={{
                marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)',
              }}>
                <strong>Volver a empezar</strong>
                <p className="sub" style={{ margin: '4px 0 8px' }}>
                  Reiniciar deja la orden viva y le quita lo andado: cancela la
                  cotización, los vales y las solicitudes abiertas, y la devuelve
                  a «aceptado» como si acabara de llegar. Es para la orden que se
                  armó mal —se cotizó lo que no era, se pidió la pieza
                  equivocada— y a la que corregir renglón por renglón sale más
                  caro que empezar de nuevo. <strong>No es cancelarla.</strong>
                </p>

                {/* Apagado y diciendo por qué, no escondido. Karin: «si ya
                    comenzaron un proceso deberia advertirles», y un botón
                    que se esfuma hace preguntar dónde está. */}
                {!orden.reinicio.sePuede && (
                  <ul className="aviso" style={{ margin: '8px 0', paddingLeft: 28 }}>
                    {orden.reinicio.impedimentos.map((i: { motivo: string; detalle: string }) => (
                      <li key={i.motivo}>{i.detalle}</li>
                    ))}
                  </ul>
                )}

                <button
                  disabled={!orden.reinicio.sePuede}
                  onClick={() => setReiniciando(true)}
                >
                  Reiniciar la orden
                </button>
              </div>
            )}

          </div>
        )}

        {/* Pedir la pieza desde aquí y no desde Compras: quien descubre
            que falta una refacción está mirando esta orden, y obligarlo a
            cambiar de pantalla y volver a identificarla es como se pierden
            las solicitudes. Al pedirla, la orden pasa sola a esperar
            refacciones. */}
        {/* Pedir una pieza es editar la orden, no comprar: la API lo
            exige así —`ordenes: editar`— y la pantalla preguntaba por
            `compras: crear`, que supervisor y soporte operativo no tienen.
            Resultado: los dos perfiles que descubren que falta una
            refacción eran los únicos que no podían pedirla. */}
        {puede('ordenes', 'editar')
          && !['cierre_admin', 'cancelado'].includes(orden.estatus) && (
          /* Verde: lo que la orden necesita y no se le cobra al cliente. */
          <div className="seccion interno">
            {/*
              El nombre dice de qué son, no cómo se piden.

              Se llamaba «Refacciones» a secas, al lado de la cotización
              —que también lleva refacciones—, y las dos se veían como lo
              mismo. Son cosas distintas y la diferencia es dinero: las de
              la cotización se le cobran al cliente y éstas no.
            */}
            <h2>Solicitud directa de refacciones</h2>
            <div className="ayuda" style={{ marginBottom: 10 }}>
              Piezas que la orden necesita y <strong>no se cotizan ni se le
              cobran al cliente</strong>. Se le piden a almacén directo, sin
              pasar por autorización. Lo que sí se le va a cobrar va en la
              cotización, arriba.
            </div>
            <RefaccionesDeLaOrden ordenId={id} recarga={recargaRefacciones} />
            <SolicitarRefaccion
              ordenId={id}
              /* Para avisar si a esta tienda ya se le surtió la pieza. */
              sucursalId={orden.sucursal?.id ?? null}
              onSolicitada={() => {
                setRecargaRefacciones((n) => n + 1);
                cargar();
                onCambio();
              }}
            />
          </div>
        )}

        {/* Los vales de taller externo de esta orden.
            Aquí y no en una pantalla suelta porque el vale cuelga
            siempre de una orden: crearlo desde aquí quita el paso de
            buscarla —y de escoger la que no era— y deja el gasto a la
            vista de quien mira la orden. */}
        <ValesDeLaOrden orden={orden} puede={puede} />

        {/* El PRE-PO: el documento previo a la orden de compra que manda
            el cliente. Llega en PDF y hoy vive en el correo de quien lo
            recibió, que es donde se pierde. */}
        <div className="seccion">
        <h2>PRE-PO</h2>
        {orden.evidencias.filter((e) => e.tipo === 'pre_po').length === 0 ? (
          <p className="sub">Sin PRE-PO adjunto.</p>
        ) : (
          <ul style={{ listStyle: 'none', display: 'grid', gap: 4, margin: 0, padding: 0 }}>
            {orden.evidencias.filter((e) => e.tipo === 'pre_po').map((e) => (
              <li key={e.id}>
                <button
                  className="enlace mono"
                  onClick={() => {
                    void api.descargar(`/ordenes/${id}/evidencias/${e.id}`, undefined, e.nombre);
                  }}
                >
                  {e.nombre}
                </button>
                {puede('ordenes', 'editar') && (
                  <button className="enlace" onClick={() => quitarEvidencia(e.id)}>Quitar</button>
                )}
              </li>
            ))}
          </ul>
        )}

        {puede('ordenes', 'editar') && (
          <div className="campo" style={{ marginTop: 8 }}>
            <label htmlFor="prePo">Adjuntar PRE-PO</label>
            {/* Los mismos tres formatos que la evidencia y la orden de
                servicio: la API los admite por igual para los tres tipos,
                y tres campos que aceptan cosas distintas sin razón es una
                diferencia que nadie recuerda. `image/*` se cambia por los
                tres tipos concretos por lo mismo: era más ancho que lo que
                la API acepta, así que ofrecía un GIF que iba a rechazar. */}
            <input
              id="prePo" type="file"
              accept={'image/jpeg,image/png,image/webp,application/pdf,'
                + 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
              disabled={trabajando}
              onChange={(e) => {
                const archivo = e.target.files?.[0];
                if (archivo) void subirEvidencia(archivo, 'pre_po');
                e.target.value = '';
              }}
            />
            <span className="ayuda">
              El documento previo a la orden de compra del cliente. PDF, DOCX
              o imagen.
            </span>
          </div>
        )}
        </div>

        {/* La orden de servicio firmada se muestra aparte de las fotos:
            es el papel que el cliente pide cuando discute una factura, y
            entre veinte fotos del equipo no se encuentra. */}
        {orden.evidencias.some((e) => e.tipo === 'orden_servicio') && (
          <div className="seccion">
            <h2>Orden de servicio firmada</h2>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {/* El mismo dibujo que la evidencia: antes enseñaba un
                  recuadro con «PDF» que no se podía abrir. */}
              {orden.evidencias.filter((e) => e.tipo === 'orden_servicio').map((e) => (
                <Adjunto key={e.id} ordenId={id} evidencia={e} />
              ))}
            </div>
          </div>
        )}

        <div className="seccion">
        <h2>Evidencia</h2>
        {orden.evidencias.filter((e) => e.tipo === 'foto').length === 0 ? (
          <p className="sub">Todavía no hay evidencia.</p>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {orden.evidencias.filter((e) => e.tipo === 'foto').map((e) => (
              <Adjunto
                key={e.id}
                ordenId={id}
                evidencia={e}
                onQuitar={() => quitarEvidencia(e.id)}
              />
            ))}
          </div>
        )}

        <div className="campo">
          <label htmlFor="evidencia">Agregar evidencia</label>
          <input
            id="evidencia"
            ref={archivoRef}
            type="file"
            /* Documentos además de fotos: lo que sostiene una orden no
               siempre es una foto —un dictamen del taller, un informe—, y
               sin poder adjuntarlo el papel vive en el correo de quien lo
               recibió. El `accept` sólo sugiere; lo que decide es la API,
               que mira el contenido y no la extensión. */
            accept={'image/jpeg,image/png,image/webp,application/pdf,'
              + 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
            disabled={trabajando}
            onChange={(e) => {
              const archivo = e.target.files?.[0];
              if (archivo) void subirEvidencia(archivo);
            }}
          />
          <span className="ayuda">
            Fotos (JPG, PNG, WebP) o documentos (PDF, DOCX), hasta 10 MB.
          </span>
        </div>
        </div>

        <div className="seccion">
        <h2>Bitácora</h2>
        <div className="campo">
          <textarea
            rows={2}
            value={nota}
            placeholder="Nota para la bitácora. No se puede editar ni borrar después."
            onChange={(e) => setNota(e.target.value)}
          />
          <button
            onClick={agregarNota}
            disabled={trabajando || nota.trim().length < 5}
            style={{ justifySelf: 'start' }}
          >
            Anotar
          </button>
        </div>

        <ol style={{ listStyle: 'none', display: 'grid', gap: 10 }}>
          {orden.bitacora.map((b) => (
            <li key={b.id} className="aviso">
              <strong>{fechaCorta(b.creadoEn)}</strong>
              {b.registradoPor && ` · ${b.registradoPor}`}
              {b.estatusNuevo && (
                <> · {ETIQUETAS_ESTATUS[b.estatusAnterior ?? ''] ?? b.estatusAnterior ?? 'inicio'}
                  {' → '}{ETIQUETAS_ESTATUS[b.estatusNuevo] ?? b.estatusNuevo}</>
              )}
              <div>{b.descripcion}</div>
            </li>
          ))}
        </ol>
        </div>

      </Dialogo>

      {corrigiendo && (
        <Corregir
          orden={orden}
          onCerrar={() => setCorrigiendo(false)}
          onHecho={() => { setCorrigiendo(false); cargar(); onCambio(); }}
        />
      )}

      {reiniciando && orden.reinicio && (
        <Reiniciar
          ordenId={id}
          folio={orden.folio}
          dictamen={orden.reinicio}
          onCerrar={() => setReiniciando(false)}
          onHecho={() => { setReiniciando(false); cargar(); onCambio(); }}
        />
      )}

      {accion && (
        <EjecutarAccion
          ordenId={id}
          accion={accion}
          cotizacion={orden.cotizacion}
          /* Lo que la matriz de cobertura propuso al abrirse la orden. */
          costoQueProponeLaMatriz={orden.tieneCosto}
          /* La decisión que falta cuando no hay cotización con qué facturar. */
          hayQueDecidirSiSeCobra={orden.hayQueDecidirSiSeCobra}
          tecnicos={tecnicosDeLaZona ?? tecnicos}
          /* Lo que la visita ya sabe, para no teclearlo otra vez. */
          cierrePropuesto={orden.preventivo?.cierrePropuesto ?? null}
          /* Para pedir los servicios dentro del cierre, si es preventiva. */
          esPreventiva={orden.tipo === 'preventivo'}
          sucursalId={orden.sucursal?.id ?? null}
          /* Lo que la visita ya registró, para no proponer otra cosa. */
          serviciosRegistrados={orden.preventivo?.servicios ?? []}
          /* Lo que se paga y no se cobra, para confirmarlo al cerrar. */
          noSeCobra={orden.noSeCobra}
          onCerrar={() => setAccion(null)}
          onHecho={() => { setAccion(null); cargar(); onCambio(); }}
        />
      )}
    </>
  );
}

/**
 * Reiniciar la orden: enumerar lo que se pierde, y pedir el motivo.
 *
 * Karin, de la reunión con APRISA: «el tema de reiniciar esta perfecto
 * version acotada como sugieres, si ya comenzaron un proceso deberia
 * advertirles».
 *
 * Las advertencias son lo que se va a deshacer, no lo que lo impide: si
 * algo lo impidiera, el botón estaría apagado y este diálogo no se
 * abriría. Van enumeradas y no resumidas porque cada una manda a hacer
 * algo distinto antes de confirmar —hablarle al taller, avisarle al
 * proveedor— y un «se cancelarán varias cosas» no manda a ninguna parte.
 */
function Reiniciar({ ordenId, folio, dictamen, onCerrar, onHecho }: {
  ordenId: string;
  folio: string;
  dictamen: NonNullable<OrdenDetalle['reinicio']>;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  // El mismo mínimo que exige la API. Comprobarlo aquí es lo que evita
  // que el botón prometa algo que va a fallar.
  const listo = motivo.trim().length >= 10;

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/ordenes/${ordenId}/reiniciar`, { motivo: motivo.trim() });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Reiniciar la orden ${folio}`}
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="peligro" onClick={confirmar} disabled={!listo || trabajando}>
            {trabajando ? 'Reiniciando…' : 'Reiniciar la orden'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p>
        La orden vuelve a <strong>«aceptado»</strong>, como si acabara de
        llegar. No se cancela: se puede volver a cotizar y atender.
      </p>

      {dictamen.advertencias.length > 0 ? (
        <div className="aviso">
          <strong>Esto se va a deshacer:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {dictamen.advertencias.map(
              (a: { detalle: string }) => <li key={a.detalle}>{a.detalle}</li>,
            )}
          </ul>
        </div>
      ) : (
        <p className="sub">No hay nada capturado que se vaya a perder.</p>
      )}

      <div className="campo">
        <label htmlFor="motivo-reinicio">Por qué se reinicia *</label>
        <textarea
          id="motivo-reinicio" rows={3} value={motivo} maxLength={500}
          placeholder="Se cotizó el equipo equivocado"
          onChange={(e) => setMotivo(e.target.value)}
        />
        <span className="ayuda">
          Es lo único que va a quedar para entender por qué la orden volvió a
          empezar: el reinicio borra el rastro de lo que se hizo. Queda en la
          bitácora con tu nombre.
        </span>
      </div>
    </Dialogo>
  );
}

function EjecutarAccion({
  ordenId, accion, cotizacion, costoQueProponeLaMatriz, hayQueDecidirSiSeCobra,
  tecnicos, cierrePropuesto,
  esPreventiva, sucursalId, serviciosRegistrados, noSeCobra, onCerrar, onHecho,
}: {
  ordenId: string;
  accion: AccionOrden;
  /**
   * Lo que se le paga al taller y no se le cobra al cliente.
   *
   * Se enseña dentro del cierre y hay que confirmarlo: el aviso de la
   * ficha se lee al entrar, pero la decisión se toma aquí, y un aviso que
   * quedó tres pantallazos arriba no es un aviso.
   */
  noSeCobra: OrdenDetalle['noSeCobra'];
  /** Lo ya cotizado, para poder decir qué se le va a pedir a almacén. */
  cotizacion: RenglonCotizacion[];
  /**
   * Lo que la matriz de cobertura propuso, para enseñarlo junto a la
   * casilla. Propone, no decide: sabe de áreas y de formatos de tienda,
   * no de lo que pasó en sitio.
   */
  costoQueProponeLaMatriz: boolean;
  /**
   * Si al cerrar hay que decidir si se le cobra al cliente.
   *
   * La correctiva que dice generar costo y no tiene cotización. Lo
   * calcula la API —`lista-para-facturar`— y no la pantalla: es la misma
   * regla que decide si el cierre pasa, y tenerla en los dos lados es
   * como el botón acaba ofreciendo lo que el servicio rechaza.
   */
  hayQueDecidirSiSeCobra: boolean;
  tecnicos: Opcion[];
  /**
   * El cierre que propone la visita preventiva acusada, si la hay.
   *
   * Cerrar sigue exigiendo resultado y descripción —es lo que sostiene la
   * factura y el historial del equipo— pero el dato ya existe: está en la
   * visita, con su OS, su fecha y los servicios que se registraron. Esto
   * es lo que soporte tecleaba a mano después de emparejar la OS con la
   * OT, y lo único que hace falta ahora es leerlo y confirmarlo.
   */
  cierrePropuesto: { resultado: string; descripcionTrabajo: string } | null;
  /**
   * Si hay que pedir los servicios de la visita dentro del cierre.
   *
   * Karin: «cuando cierras una OT preventiva no me sale el menú de los
   * servicios que se hicieron, y eso me obliga a ir a buscarla a
   * preventivo y ponerlo, creo que debería estar eso de alguna manera
   * ligado para no hacerlo 2 veces».
   *
   * Eran dos capturas del mismo dato en dos pantallas, y la segunda se
   * hacía de memoria días después. Ahora se piden aquí: son los
   * renglones de la factura de una preventiva, así que el sitio donde se
   * decide que el trabajo terminó es el sitio donde se dice qué se hizo.
   */
  esPreventiva: boolean;
  /** De dónde salen los servicios pactados que se proponen. */
  sucursalId: string | null;
  /**
   * Lo que la visita ya registró, si se acusó antes de que llegara la OT.
   *
   * Es lo que manda. Si el técnico hizo dos de tres y alguien lo capturó
   * en Preventivo, el cierre tiene que abrir con esos dos: proponer los
   * tres pactados volvería a meter el que no se hizo, y cerrar lo
   * facturaría. Los pactados son la propuesta de cuando **nadie** ha
   * dicho nada, que es el otro caso.
   */
  serviciosRegistrados: NonNullable<OrdenDetalle['preventivo']>['servicios'];
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [tecnicoId, setTecnicoId] = useState('');
  const [fechaProgramada, setFechaProgramada] = useState('');
  const [motivo, setMotivo] = useState('');
  const [resultado, setResultado] = useState(
    cierrePropuesto?.resultado ?? 'reparacion_completada',
  );
  const [descripcionTrabajo, setDescripcionTrabajo] = useState(
    cierrePropuesto?.descripcionTrabajo ?? '',
  );
  const [firma, setFirma] = useState('firmado_en_sitio');
  const [noOsCliente, setNoOsCliente] = useState('');
  /** Que ya vio lo que se paga y no se cobra, y aun así cierra. */
  const [aceptaQueNoSeCobra, setAceptaQueNoSeCobra] = useState(false);
  /**
   * Que la orden la cubre la iguala, dicho al cerrar.
   *
   * Para la correctiva que dice generar costo y no tiene cotización.
   * Karin, sobre la OT-1801384516: «¿como se supone que la vamos a
   * cerrar, o que nos falta?». No le faltaba nada: era un callejón.
   */
  const [confirmaQueNoSeCobra, setConfirmaQueNoSeCobra] = useState(false);
  const [precio, setPrecio] = useState('');
  /**
   * La cotización se abre con lo que ya está capturado.
   *
   * Antes empezaba en blanco, así que corregirla era volver a teclear
   * todo —y de paso perder las firmas—. Ahora se traen los renglones
   * guardados y la API compara: lo que no se toca conserva su firma.
   *
   * Con la cotización vacía, un renglón en blanco para empezar.
   */
  const [proveedores, setProveedores] = useState<{ id: string; nombre: string }[]>([]);

  const [renglones, setRenglones] = useState<RenglonEnCaptura[]>(
    () => (cotizacion.length > 0
      ? cotizacion.map(renglonDesdeLoGuardado)
      // Con la cotización vacía, un renglón con lo que propone la matriz.
      // No es una respuesta dada por omisión: es la propuesta, y el texto
      // de arriba dice de dónde salió para que se pueda contradecir.
      : [{ ...renglonVacio(), cubiertoPorIguala: !costoQueProponeLaMatriz }]),
  );

  const [servicios, setServicios] = useState<ServicioReparacion[]>([]);
  // La orden de servicio firmada se sube antes de confirmar el cierre: es
  // un archivo, y colgarlo del mismo botón obligaría a mandarlo dentro de
  // la transición. Si el cierre falla, el documento ya está guardado en la
  // orden, que es donde tiene que estar de todos modos.
  const [documento, setDocumento] = useState<{ id: string; nombre: string } | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const pide = (dato: string) => accion.requiere?.includes(dato as never) ?? false;

  /**
   * Los servicios preventivos, dentro del cierre.
   *
   * `hechos` en nulo significa «todavía no cargaron». Lo que llega
   * palomeado depende de si alguien ya dijo qué se hizo:
   *
   *   ya se capturó  se abren esos, y nada más. Si el técnico hizo dos
   *                  de tres y soporte lo registró en Preventivo,
   *                  proponer los tres volvería a meter el que no se
   *                  hizo, y cerrar lo facturaría.
   *   nadie ha dicho se abren los pactados, todos. La visita normal
   *                  cumple lo contratado, y obligar a palomear la misma
   *                  lista cada vez es la clase de trámite que acaba en
   *                  que nadie captura nada.
   *
   * En los dos casos se puede corregir antes de cerrar, que es de lo que
   * se trata: ver lo que hay y confirmarlo sin ir a otra pantalla.
   */
  /**
   * El cierre sólo pide los servicios si nadie los capturó todavía.
   *
   * Karin, siguiendo el flujo entero: «te pide que desde órdenes le
   * agregues el área y el servicio, a pesar de que lo seleccionaste
   * desde preventivo».
   *
   * El acuse de la visita es donde se dice qué se hizo, y es donde se
   * dice primero: el técnico va, soporte acusa con la OS, y la OT llega
   * después. Volver a preguntarlo aquí no era una confirmación sino una
   * segunda captura del mismo dato, con la mitad de la información —y
   * con la posibilidad de contradecir lo que ya estaba.
   *
   * Cuando la visita ya lo dijo, aquí se enseña y no se toca. Cuando no
   * lo dijo nadie, se sigue pudiendo capturar: es la salida para la
   * orden que llegó sin visita acusada, y cerrarla sin renglones la
   * dejaría sin qué facturar.
   */
  /**
   * Y ahora también se pueden corregir desde aquí.
   *
   * Karin: «ahora que si la cierro desde la OT no puedo elegir el
   * servicio que se le hizo o si? tendriamos que poder cerrarla de la
   * misma manera».
   *
   * Sí se podía, pero sólo cuando nadie los había dicho. Con la visita
   * acusada se enseñaban y no se tocaban, y entonces descubrir aquí que
   * falta uno obligaba a cambiar de pantalla para una corrección de un
   * clic.
   *
   * El motivo original de bloquearlo era evitar dos capturas que se
   * contradijeran. Ya no puede pasar: los dos caminos rehacen la lista
   * desde cero —`registrarLoQueSeHizoAlCerrar` borra y reescribe— así
   * que lo que vale es siempre lo último que se dijo, lo diga quien lo
   * diga. Y desde que existe «Corregir el acuse» el otro lado tampoco es
   * de un solo uso.
   *
   * Lo que **no** cambia: las casillas abren con lo que la visita ya
   * registró, nunca con los pactados. La diferencia es quién lo afirmó,
   * y palomear por omisión lo contratado convierte un clic que nadie dio
   * en un renglón facturado.
   */
  const pideServicios = pide('cierre') && esPreventiva;
  const [pactados, setPactados] = useState<ServiciosPactados | null>(null);
  const [hechos, setHechos] = useState<Set<string> | null>(null);
  const [equiposPorServicio, setEquiposPorServicio] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!pideServicios || !sucursalId) return;

    api.get<ServiciosPactados>('/servicios-preventivos/pactados', { sucursalId })
      .then((r) => {
        setPactados(r);

        /**
         * Sólo lo que alguien ya dijo que se hizo. Los pactados, no.
         *
         * Karin, después de cerrar una con dos servicios sin querer: «no
         * me pongas los servicios premarcados […] déjalos inactivos para
         * que soporte sólo seleccione el que va».
         *
         * La diferencia entre los dos casos es quién lo afirmó. Si la
         * visita ya registró qué se hizo, eso es una captura y se abre
         * palomeada: desmarcarla al abrir borraría trabajo de alguien.
         * Los pactados son lo que la tienda tiene contratado, que no es
         * lo mismo que lo que se hizo, y palomearlos convertía un clic
         * que nadie dio en un renglón facturado.
         *
         * Siempre el id del catálogo —`servicioPreventivoId`— y nunca el
         * del renglón: es el que casa con los pactados y el que la API
         * acepta de vuelta.
         */
        setHechos(new Set(serviciosRegistrados.map((x) => x.servicioPreventivoId)));

        // Y los equipos que ya se dictaron, uno por renglón, como se
        // capturaron. Sin esto, cerrar los borraría.
        setEquiposPorServicio(Object.fromEntries(
          serviciosRegistrados
            .filter((x) => x.equipos.length > 0)
            .map((x) => [
              x.servicioPreventivoId, x.equipos.map((e) => e.nombre).join('\n'),
            ]),
        ));
      })
      .catch(() => { /* Lo dirá el cierre: sin pactados no hay qué palomear. */ });
  }, [pideServicios, sucursalId, serviciosRegistrados]);

  /**
   * Los que se pueden palomear: los pactados, más los que la visita ya
   * registró aunque hoy ya no estén pactados.
   *
   * Lo segundo pasa cuando el catálogo cambió entre la visita y el
   * cierre. Sin esto, el servicio desaparecería de la lista pero
   * seguiría en la orden, y cerrar lo borraría sin que nadie lo viera.
   *
   * Se juntan por `id` en un mapa en vez de filtrar los repetidos: así
   * la lista no puede enseñar dos veces el mismo servicio aunque los dos
   * lados discrepen. Karin se encontró con eso —«me está duplicando un
   * servicio»— por mandar el identificador equivocado, y un renglón
   * repetido en la pantalla del cierre es un servicio facturado dos
   * veces esperando a que alguien lo palomee.
   */
  const pactadosDeLaSucursal = [...new Map([
    ...(pactados?.servicios.filter((x) => x.pactado) ?? []),
    ...serviciosRegistrados.map((x) => ({
      id: x.servicioPreventivoId, codigo: x.codigo, nombre: x.nombre, pactado: true,
    })),
  ].map((x) => [x.id, x] as const)).values()];

  /**
   * Cuántos de los que ya están tienen firma.
   *
   * Sirve para avisar antes de guardar, que es cuando todavía se puede
   * reconsiderar. No se calcula aquí qué renglón la pierde —eso lo decide
   * la API comparando, y tener la regla en dos sitios es tenerla en
   * ninguno— pero sí se dice que hay firmas de por medio.
   */
  const firmados = cotizacion.filter((r) => r.autorizadoEn !== null).length;
  const editandoCotizacion = pide('renglones') && cotizacion.length > 0;

  /**
   * Cuántos conceptos se le cobran al cliente.
   *
   * Es lo que la API deduce en `laOrdenGeneraCosto` y lo que guarda en
   * `tiene_costo`. Se cuenta aquí para enseñar el resultado mientras se
   * captura: la pregunta que había arriba se contestaba antes de saber lo
   * que iba a tener la cotización, y ésta se contesta sola.
   */
  const cobrados = renglones.filter((r) => !r.cubiertoPorIguala).length;

  // Las piezas que todavía no se han pedido. Las ya pedidas no se
  // vuelven a pedir —eso lo garantiza la API— y anunciarlas aquí sería
  // prometer algo que no va a pasar.
  const porPedir = cotizacion.filter((r) => r.tipo === 'refaccion' && !r.enAlmacen);

  // El catálogo de servicios se pide entero: son unas cuantas decenas y
  // caben en un desplegable. El de refacciones no —son cientos— y se
  // busca por renglón, con el mismo buscador que usa Compras.
  useEffect(() => {
    if (!pide('renglones')) return;

    api.get<ServicioReparacion[]>('/servicios-reparacion/opciones')
      .then(setServicios)
      .catch(() => undefined);

    /**
     * A quién se le puede pagar un servicio.
     *
     * Los mismos proveedores que usa Compras, y ahí están también los
     * técnicos: se dieron de alta como proveedores justo para poder
     * pagarles el trabajo por esta vía.
     */
    api.get<Pagina<{ id: string; nombre: string }>>('/proveedores', {
      estado: 'activo', limite: 200,
    })
      .then((r) => setProveedores(r.data))
      .catch(() => undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accion.a]);

  function cambiarRenglon(llave: number, campo: keyof RenglonEnCaptura, valor: string) {
    setRenglones((rs) => rs.map((r) => (r.llave === llave ? { ...r, [campo]: valor } : r)));
  }

  /**
   * Elegir del catálogo llena el renglón; volver a «capturar a mano» sólo
   * suelta la referencia y deja lo escrito. Borrar lo que alguien ya
   * corrigió sería perder su trabajo por cambiar un desplegable.
   */
  /**
   * Escoger la pieza llena el renglón con lo que sabe el catálogo.
   *
   * Sólo el código y el nombre. El costo promedio y el precio sugerido no
   * se traen aunque existan: aquí no se capturan precios, los pone
   * Almacén al autorizar, y proponerlos desde esta pantalla sería adivinar
   * en nombre de quien sí tiene el dato.
   */
  function elegirRefaccion(llave: number, f: Refaccion | null) {
    setRenglones((rs) => rs.map((r) => {
      if (r.llave !== llave) return r;
      if (!f) return { ...r, refaccion: null };

      return { ...r, refaccion: f, codigo: f.codigo, descripcion: f.nombre };
    }));
  }

  /**
   * Cambiar de trabajo a pieza suelta la referencia del otro catálogo.
   *
   * Lo escrito se queda: si alguien ya tecleó la descripción y luego se
   * da cuenta de que era una pieza, borrarla sería perder su trabajo por
   * cambiar un desplegable. Lo que no puede quedarse es la referencia,
   * porque la API rechaza un servicio con refacción y al revés.
   */
  function cambiarTipo(llave: number, tipo: RenglonEnCaptura['tipo']) {
    setRenglones((rs) => rs.map((r) => (r.llave === llave
      // El costo se va con el tipo: era el de un servicio y una pieza no
      // lleva costo capturado. Dejarlo mandaría a la API un dato que va a
      // rechazar, con un mensaje que no explica de dónde salió.
      ? { ...r, tipo, servicioReparacionId: '', refaccion: null, precioCosto: '' }
      : r)));
  }

  function elegirServicio(llave: number, id: string) {
    const s = servicios.find((x) => x.id === id);

    setRenglones((rs) => rs.map((r) => {
      if (r.llave !== llave) return r;
      if (!s) return { ...r, servicioReparacionId: '' };

      return {
        ...r,
        servicioReparacionId: s.id,
        codigo: s.codigo,
        descripcion: s.descripcion,
      };
    }));
  }

  const listo =
    (!pide('tecnico') || Boolean(tecnicoId)) &&
    (!pide('motivo') || motivo.trim().length >= 5) &&
    // El mismo motivo que exige la API al tumbar firmas. Comprobarlo aquí
    // es lo que evita que el botón prometa algo que va a fallar.
    (!(editandoCotizacion && firmados > 0) || motivo.trim().length >= 5) &&
    (!pide('renglones') || renglones.every(completo)) &&
    (!pide('precio') || Number(precio) > 0) &&
    (!pide('cierre') || descripcionTrabajo.trim().length >= 10) &&
    // Y que hayan visto lo que se paga sin cobrarse. La API se niega
    // igual; comprobarlo aquí es lo que evita que el botón prometa un
    // cierre que va a fallar.
    (!pide('cierre') || noSeCobra.conceptos.length === 0 || aceptaQueNoSeCobra) &&
    // Y la decisión que falta cuando no hay cotización con qué facturar.
    (!pide('cierre') || !hayQueDecidirSiSeCobra || confirmaQueNoSeCobra) &&
    /**
     * Una preventiva no cierra sin al menos un servicio.
     *
     * Karin: «pero si estas validando en el caso de preventivos que
     * obligatorio debe de ir un servicio para poderla cerrar, correcto?».
     *
     * Correcto, y la API lo exige desde hace tiempo: una preventiva se
     * factura siempre y sus renglones son los servicios, así que sin
     * ninguno no hay qué cobrar. Lo que faltaba era decirlo aquí: el
     * botón se dejaba pulsar y el rechazo llegaba al final, que se lee
     * como que el sistema está roto.
     *
     * Sólo cuando la pantalla los está pidiendo. Si no los pide —una
     * correctiva— no tiene nada que comprobar.
     */
    (!pideServicios || (hechos?.size ?? 0) > 0);

  async function subirOrdenServicio(archivo: File) {
    setError(null);
    setSubiendo(true);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', await aligerar(archivo));

      const respuesta = await fetchAutenticado(
        `/api/v1/ordenes/${ordenId}/evidencias?tipo=orden_servicio`,
        { method: 'POST', body: cuerpo },
      );

      if (!respuesta.ok) {
        const texto = await respuesta.text();
        const cuerpoError = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};
        throw new ErrorApi(
          respuesta.status,
          String(cuerpoError.codigo_negocio ?? 'error'),
          String(cuerpoError.detail ?? 'No se pudo subir la orden de servicio.'),
        );
      }

      const guardada = (await respuesta.json()) as { id: string };
      setDocumento({ id: guardada.id, nombre: archivo.name });
    } catch (e) {
      setError(e);
    } finally {
      setSubiendo(false);
    }
  }

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/ordenes/${ordenId}/transicion`, {
        hacia: accion.a,
        // Cuál de las salidas, cuando el mismo estado se alcanza de dos
        // maneras. Hoy sólo la revisión del cliente.
        clave: accion.clave,
        tecnicoId: tecnicoId || undefined,
        fechaProgramada: fechaProgramada ? new Date(fechaProgramada).toISOString() : undefined,
        motivo: motivo.trim() || undefined,
        resultado: pide('cierre') ? resultado : undefined,
        descripcionTrabajo: pide('cierre') ? descripcionTrabajo.trim() : undefined,
        firma: pide('cierre') ? firma : undefined,
        noOsCliente: pide('cierre') ? noOsCliente.trim() || undefined : undefined,
        aceptaQueNoSeCobra: pide('cierre') ? aceptaQueNoSeCobra : undefined,
        confirmaQueNoSeCobra: pide('cierre') && hayQueDecidirSiSeCobra
          ? confirmaQueNoSeCobra
          : undefined,
        /**
         * Lo que se hizo, si es una preventiva.
         *
         * Va sólo cuando los pactados ya cargaron: mandar una lista
         * vacía porque todavía no llegan sería decirle a la API «no se
         * hizo ninguno», que es lo contrario de lo que pasa. Sin la
         * lista, la API escribe los pactados, que es la suposición
         * correcta de una visita normal.
         */
        /**
         * Siempre explícito cuando la pantalla los pidió, aunque esté
         * vacío. Mandar `undefined` significa «no dije nada» y la API lo
         * traduce como «los pactados de la tienda», que es exactamente
         * lo que se acaba de quitar. Una lista vacía dice «ninguno», y
         * entonces el cierre se niega por no tener qué facturar, que es
         * la respuesta correcta.
         */
        serviciosPreventivos: pideServicios ? [...(hechos ?? [])] : undefined,
        equiposPreventivos: pideServicios && hechos
          ? [...hechos]
            .map((servicioPreventivoId) => ({
              servicioPreventivoId,
              equipos: (equiposPorServicio[servicioPreventivoId] ?? '')
                .split('\n')
                .map((t) => t.trim())
                .filter((t) => t.length > 0)
                .map((texto) => ({ texto })),
            }))
            .filter((g) => g.equipos.length > 0)
          : undefined,
        renglones: pide('renglones')
          ? renglones.map((r) => ({
            // El renglón que ya existía. Es lo que le conserva la firma a
            // lo que no se tocó.
            id: r.id || undefined,
            tipo: r.tipo,
            servicioReparacionId: r.servicioReparacionId || undefined,
            // La pieza recién escogida, o la que el renglón ya traía.
            refaccionId: r.refaccion?.id ?? (r.refaccionId || undefined),
            codigo: r.codigo.trim() || undefined,
            descripcion: r.descripcion.trim(),
            cantidad: Number(r.cantidad),
            // Sólo del servicio, y sólo si se capturó. Vacío se va como
            // ausente y no como cero: la API distingue «no se sabe» de
            // «es gratis», y mandar un cero perdería esa diferencia.
            precioCosto: r.tipo === 'servicio' && r.precioCosto !== ''
              ? Number(r.precioCosto)
              : undefined,
            // Sólo del servicio, por lo mismo que el costo: en una
            // refacción, a quién se le compró lo dice su orden de compra.
            proveedorId: r.tipo === 'servicio' ? r.proveedorId || undefined : undefined,
            // Quién paga este concepto. Va siempre y no sólo cuando
            // cambió: la API lo usa para decidir el precio de venta, y
            // omitirlo haría que lo recalcule desde la propuesta y pise
            // lo que esta persona acaba de marcar.
            cubiertoPorIguala: r.cubiertoPorIguala,
          }))
          : undefined,
        precio: pide('precio') ? Number(precio) : undefined,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={accion.etiqueta}
      /* Ancho sólo cuando se captura la cotización. Los renglones traen
         cantidad, costo, venta e importe en la misma fila, y a 780px esas
         cuatro columnas se salen del diálogo: se ve cortado por el lado.
         Las demás acciones —asignar, cancelar, cerrar— siguen estrechas,
         que es lo que les corresponde. */
      ancho={pide('renglones')}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={confirmar} disabled={trabajando || !listo}>
            {trabajando ? 'Guardando…' : accion.etiqueta}
          </button>
        </>
      }
    >
      <Error error={error} />
      <p className="sub">{accion.explicacion}</p>

      {/* Lo que va a pasar al confirmar, antes de confirmarlo. Aceptar
          por el cliente saca piezas del anaquel, y saber cuáles después
          de haberlo hecho no sirve de nada. */}
      {accion.a === 'en_proceso' && porPedir.length > 0 && (
        <div className="aviso">
          Al aceptar se le van a pedir a almacén{' '}
          <strong>{porPedir.length} refacción(es)</strong>:{' '}
          {porPedir.map((r) => `${r.cantidad} × ${r.descripcion}`).join(', ')}. La
          orden queda esperándolas.
        </div>
      )}

      {pide('tecnico') && (
        <>
          <div className="campo">
            <label htmlFor="tecnicoId">Técnico *</label>
            <select
              id="tecnicoId" value={tecnicoId}
              onChange={(e) => setTecnicoId(e.target.value)}
            >
              <option value="">— elige un técnico —</option>
              {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
            <span className="ayuda">
              Si eliges uno de otra zona se permite, pero queda anotado en la bitácora.
            </span>
          </div>

          <div className="campo">
            <label htmlFor="fechaProgramada">Fecha programada</label>
            <input
              id="fechaProgramada" type="datetime-local" value={fechaProgramada}
              onChange={(e) => setFechaProgramada(e.target.value)}
            />
          </div>
        </>
      )}

      {pide('cierre') && (
        <>
          {/* Lo que se paga y no se cobra, donde se toma la decisión.

              Karin: «me quedo la duda que si desde un paso anterior
              marque como "si tiene costo" se entiende que se cobra al
              cliente». No: el sí/no dice **si** se le cobra a la orden,
              la cotización dice **qué** y **por cuánto**, y un concepto
              del vale que nunca llegó a la cotización cae entre las dos.

              La casilla no está premarcada y sin ella el botón de cerrar
              no se enciende. No es un bloqueo —a veces de verdad no se
              cobra, y obligar a inventar un renglón en cero para
              destrabar la orden sería peor— pero tampoco se puede pasar
              de largo: un aviso que nadie tiene que leer, nadie lo lee. */}
          {noSeCobra.conceptos.length > 0 && (
            <div className="aviso error">
              <strong>Se paga y no se cobra.</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                {noSeCobra.conceptos.map((c: { vale: string; descripcion: string; importe: number }) => (
                  <li key={`${c.vale}-${c.descripcion}`}>
                    {c.vale}: «{c.descripcion}» por {dinero(c.importe)}
                  </li>
                ))}
              </ul>
              <p style={{ margin: '6px 0' }}>
                Son {dinero(noSeCobra.total)} que se le pagan al taller y no
                entran en la factura del cliente. Si el trabajo sí se cobra,
                cancela el cierre y pásalo a la cotización con «Cobrar al
                cliente» desde el vale.
              </p>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={aceptaQueNoSeCobra}
                  onChange={(e) => setAceptaQueNoSeCobra(e.target.checked)}
                />
                <span>
                  Lo revisé: este trabajo no se le cobra al cliente. Queda mi
                  nombre y el importe en la bitácora de la orden.
                </span>
              </label>
            </div>
          )}

          {/*
            La pregunta que antes era un atasco.

            Karin, sobre la OT-1801384516: «se inicio el trabajo pero
            dice "la orden no tiene cotización, así que no hay conceptos
            que facturar" […] ¿como se supone que la vamos a cerrar, o
            que nos falta?».

            No le faltaba un dato. La orden nació con costo —lo dijo la
            matriz— y se arrancó por el camino que no cotiza, así que el
            cierre pedía unos conceptos que por ahí no pueden existir.
            Antes era un impedimento y no tenía salida; ahora es una
            decisión, porque las dos respuestas son legítimas y sólo una
            persona las distingue.

            La raíz ya se arregló en el botón de «Empezar sin cotizar».
            Esto es la red para las que quedaron así y para cualquier
            camino que se escape.
          */}
          {hayQueDecidirSiSeCobra && (
            <div className="aviso">
              <strong>¿Se le cobra algo al cliente?</strong>
              <p style={{ margin: '6px 0' }}>
                Esta orden dice que genera costo, pero no tiene cotización,
                así que no hay conceptos con qué armar la factura. Si de
                verdad se le cobra, cancela el cierre y captura la
                cotización con «Registrar cotización».
              </p>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={confirmaQueNoSeCobra}
                  onChange={(e) => setConfirmaQueNoSeCobra(e.target.checked)}
                />
                <span>
                  La cubre la iguala: esta orden <strong>no se factura</strong>.
                  Queda mi nombre en la bitácora.
                </span>
              </label>
            </div>
          )}

          {/*
            Lo que la visita ya sabía, escrito para revisarse.

            Se dice que viene de ahí porque un texto que aparece solo en
            un campo obligatorio invita a darle enviar sin leerlo, y esto
            es lo que sostiene la factura. Se puede corregir entero.
          */}
          {cierrePropuesto && (
            <div className="aviso">
              El resultado y la descripción vienen de lo que se registró
              en la visita preventiva. Revísalos y corrige lo que haga
              falta antes de cerrar.
            </div>
          )}

          <div className="campo">
            <label htmlFor="resultado">Resultado *</label>
            <select id="resultado" value={resultado} onChange={(e) => setResultado(e.target.value)}>
              <option value="reparacion_completada">Reparación completada</option>
              <option value="preventivo_completado">Preventivo completado</option>
              <option value="sin_solucion">Sin solución</option>
              <option value="cancelado_por_cliente">Cancelado por el cliente</option>
            </select>
          </div>

          <div className="campo">
            <label htmlFor="descripcionTrabajo">Trabajo realizado *</label>
            <textarea
              id="descripcionTrabajo" rows={4} value={descripcionTrabajo}
              onChange={(e) => setDescripcionTrabajo(e.target.value)}
            />
            <span className="ayuda">
              Es lo que sostiene la factura y el historial del equipo. Mínimo 10 caracteres.
            </span>
          </div>

          {/* Los servicios de la visita, aquí y no en otra pantalla.

              Karin: «cuando cierras una OT preventiva no me sale el menú
              de los servicios que se hicieron, y eso me obliga a ir a
              buscarla a preventivo y ponerlo». Son los renglones de la
              factura de una preventiva, así que el sitio donde se decide
              que el trabajo terminó es el sitio donde se dice qué se
              hizo. Cerrar los escribe en la visita y la deja acusada.

              Y se pueden corregir, los hubiera dicho la visita o no:
              «tendriamos que poder cerrarla de la misma manera». Vienen
              palomeados los que ya estaban —el texto de abajo lo dice—
              así que revisar no borra, y marcar uno de más es un clic en
              vez de un viaje a otra pantalla. */}

          {pideServicios && (
            <div className="campo">
              <label>Servicios que se hicieron</label>
              {pactadosDeLaSucursal.length === 0 ? (
                <span className="ayuda">
                  Esta sucursal no tiene servicios preventivos pactados. Se
                  capturan en Administración → Precios del preventivo;
                  mientras no los tenga, no hay qué facturar y el cierre se
                  va a negar.
                </span>
              ) : (
                <>
                  {pactadosDeLaSucursal.map((x) => (
                    <div key={x.id} style={{ marginBottom: 8 }}>
                      <label style={{ display: 'block', fontWeight: 400 }}>
                        <input
                          type="checkbox"
                          checked={hechos?.has(x.id) ?? false}
                          onChange={() => setHechos((antes) => {
                            const ahora = new Set(antes ?? []);
                            if (ahora.has(x.id)) ahora.delete(x.id);
                            else ahora.add(x.id);
                            return ahora;
                          })}
                        />{' '}
                        {x.codigo} · {x.nombre}
                      </label>

                      {/* Uno por renglón y no un campo con comas: son
                          nombres que llevan números —«Cámara 2»— y las
                          comas se usan al describir. */}
                      {hechos?.has(x.id) && (
                        <textarea
                          rows={2}
                          value={equiposPorServicio[x.id] ?? ''}
                          onChange={(e) => setEquiposPorServicio((antes) => ({
                            ...antes, [x.id]: e.target.value,
                          }))}
                          placeholder="Equipos, uno por renglón"
                          style={{ marginLeft: 22, width: 'calc(100% - 22px)' }}
                        />
                      )}
                    </div>
                  ))}
                  <span className="ayuda">
                    {serviciosRegistrados.length > 0
                      ? 'Vienen palomeados los que ya se registraron en la'
                        + ' visita. Revísalos y corrige si hace falta: al'
                        + ' cerrar, lo que quede aquí es lo que se factura.'
                      : 'Ninguno viene palomeado: marca sólo los que se'
                        + ' hicieron. Lo que quede aquí es lo que se'
                        + ' factura, y al cerrar se guarda en la visita.'}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Y por qué no se puede todavía, dicho donde se decide.
              La API se niega igual; esto es para no descubrirlo al
              pulsar. */}
          {pideServicios && (hechos?.size ?? 0) === 0
            && pactadosDeLaSucursal.length > 0 && (
            <Faltantes que={['marcar al menos un servicio: son los renglones'
              + ' de la factura, y una preventiva se factura siempre']}
            />
          )}

          <div className="campo">
            <label htmlFor="firma">Conformidad</label>
            <select id="firma" value={firma} onChange={(e) => setFirma(e.target.value)}>
              <option value="firmado_en_sitio">Firmado en sitio</option>
              <option value="sin_firma_cliente_ausente">Sin firma, cliente ausente</option>
              <option value="pendiente">Pendiente</option>
            </select>
          </div>

          {/* El papel que firma la sucursal es el respaldo de la factura, y
              su folio es con el que el cliente se refiere al servicio. Se
              piden al cerrar porque es cuando existen: capturarlos al dar
              de alta la orden casi nunca es posible. */}
          <div className="campo">
            <label htmlFor="noOsCliente">No. de orden de servicio</label>
            <input
              id="noOsCliente" className="mono" value={noOsCliente} maxLength={50}
              placeholder="El folio del papel que firmó la sucursal"
              onChange={(e) => setNoOsCliente(e.target.value)}
            />
            <span className="ayuda">
              Si ya se capturó al dar de alta la orden, déjalo vacío y se conserva.
            </span>
          </div>

          <div className="campo">
            <label htmlFor="ordenServicio">Orden de servicio firmada</label>
            {documento ? (
              <span className="etiqueta si">Subida: {documento.nombre}</span>
            ) : (
              <input
                id="ordenServicio"
                type="file"
                /* Documentos además de foto y escaneo. Karin: «al cerrar
                   orden en el de "orden de servicio firmada" permiteme
                   subir archivos docx y pdf». La API ya los admitía para
                   este tipo de evidencia desde que la evidencia acepta
                   documentos; era el `accept` el que no los ofrecía, así
                   que el selector de archivos los dejaba grises.

                   El `accept` sólo sugiere: lo que decide es la API, que
                   mira el contenido y no la extensión. */
                accept={'image/jpeg,image/png,image/webp,application/pdf,'
                  + 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
                disabled={subiendo || trabajando}
                onChange={(e) => {
                  const archivo = e.target.files?.[0];
                  if (archivo) void subirOrdenServicio(archivo);
                }}
              />
            )}
            <span className="ayuda">
              Foto, escaneo o documento (PDF, DOCX), hasta 10 MB. Queda en la
              evidencia de la orden.
            </span>
          </div>
        </>
      )}

      {/*
        El aviso antes de empezar, no el atasco después.

        Karin: «entonces por que nacen con costo?, no seria mejor
        preguntar desde antes de empezar». Las órdenes nacen con lo que
        dijo la matriz de cobertura, y este botón la contradice: declara
        que no se cotiza y por tanto que no se factura.

        Sólo sale cuando de verdad contradice. Si la matriz ya decía que
        la iguala cubre, el botón confirma lo que el sistema creía y no
        hay nada que avisar —un aviso que sale siempre se deja de leer—.
      */}
      {accion.declaraSinCosto && costoQueProponeLaMatriz && (
        <div className="aviso error">
          <strong>Esta orden va a dejar de facturarse.</strong>
          <p style={{ margin: '6px 0 0' }}>
            La matriz de cobertura dice que <strong>esta área sí se le cobra
            al cliente</strong> en este formato de tienda. Al empezar sin
            cotizar, la orden pasa a no generar costo y no se va a facturar:
            los conceptos de la factura son los renglones cotizados, y no va
            a haber ninguno.
          </p>
          <p style={{ margin: '6px 0 0' }}>
            Si sí se le cobra, cierra esto y usa <strong>«Pasar a
            técnico»</strong> para cotizarlo primero.
          </p>
        </div>
      )}

      {pide('renglones') && (
        <>
          {/*
            Dónde está la pregunta que había aquí.

            Era «¿Genera costo adicional?», una por orden y obligatoria,
            de la 0056. Karin preguntó cómo registrar el caso mixto —«hay
            ocasiones que puede no tener costo de forma natural por que es
            full, pero el tecnico dice, si hay que traer una refaccion esa
            si es con costo»— y la respuesta era que no se podía: marcar
            «no» ponía la venta en cero también en la refacción, y marcar
            «sí» obligaba a acordarse de poner cero en el renglón de la
            iguala.

            Así que la pregunta bajó al renglón, donde siempre vivió la
            realidad. Lo que queda arriba es el resultado, que se cuenta y
            no se contesta: cambia conforme se marcan las casillas de
            abajo, y es lo que decide si esta orden se le factura.
          */}
          <h2>¿Se le cobra algo al cliente?</h2>
          <div className="seccion acento">
            <p style={{ margin: 0 }}>
              {cobrados === 0
                ? 'No. La iguala cubre todos los conceptos de esta cotización.'
                : `Sí: se le cobra${cobrados === 1 ? '' : 'n'} ${cobrados} de `
                  + `${renglones.length} concepto${renglones.length === 1 ? '' : 's'}.`}
            </p>
            <div className="ayuda" style={{ marginTop: 6 }}>
              Sale de la casilla «Lo cubre la iguala» de cada renglón, no de
              una respuesta aparte. Así no puede decir una cosa aquí y otra
              abajo.
              {' '}
              {costoQueProponeLaMatriz
                ? 'La matriz de cobertura dice que la iguala no cubre esta área en este formato de tienda, así que los conceptos llegan como cobrados.'
                : 'La matriz de cobertura dice que la iguala cubre esta área en este formato de tienda, así que los servicios llegan ya marcados.'}
            </div>
            {cobrados === 0 && renglones.length > 0 && (
              <div className="ayuda" style={{ marginTop: 6 }}>
                Operaciones y Almacén autorizan sin precio de venta, y los
                vales TOT se generan igual para poder pagarle a quien hizo
                el trabajo.
              </div>
            )}
          </div>

          <h2>Lo que contestó el técnico</h2>
          <div className="aviso">
            Un renglón por concepto, y van los dos: los trabajos y las
            piezas que pidió el técnico. Los precios no se capturan aquí:
            <strong> Operaciones</strong> autoriza los servicios y{' '}
            <strong>Almacén</strong> las refacciones, y cada uno le pone
            precio a lo suyo. Cuando las dos firmen, aquí se confirma y se
            manda al cliente.
          </div>

          {/*
            Qué pasa con lo que ya está firmado.

            Se dice antes de guardar, que es cuando todavía se puede
            reconsiderar. Y se dice completo —lo que se conserva y lo que
            no— porque el miedo a perder las autorizaciones es lo que hace
            que una cotización mal capturada se quede como está.
          */}
          {editandoCotizacion && (
            <div className={firmados > 0 ? 'aviso' : 'ayuda'}>
              {firmados > 0 ? (
                <>
                  <strong>
                    {firmados === 1
                      ? 'Un renglón ya está autorizado.'
                      : `${firmados} renglones ya están autorizados.`}
                  </strong>{' '}
                  El que no toques conserva su firma y su precio. El que
                  cambies o quites vuelve a quedar sin firmar y le
                  reaparece a su área. Agregar uno nuevo no le quita la
                  firma a nadie.
                </>
              ) : (
                <>
                  Está la cotización que se capturó. Cambia lo que haga
                  falta, agrega los renglones que falten y guarda.
                </>
              )}
            </div>
          )}

          {renglones.map((r, i) => (
            <div key={r.llave} className="panel" style={{ marginBottom: 10 }}>
              <div className="campo">
                <label htmlFor={`tipo-${r.llave}`}>Qué es</label>
                <select
                  id={`tipo-${r.llave}`} value={r.tipo}
                  onChange={(e) => cambiarTipo(r.llave, e.target.value as RenglonEnCaptura['tipo'])}
                >
                  <option value="servicio">Trabajo o mano de obra</option>
                  <option value="refaccion">Refacción</option>
                </select>
                <span className="ayuda">
                  {r.tipo === 'refaccion'
                    ? 'Se le va a pedir a almacén cuando el cliente autorice.'
                    : 'No se le pide a nadie: es trabajo que se paga.'}
                </span>
              </div>

              {r.tipo === 'servicio' ? (
                <div className="campo">
                  <label htmlFor={`servicio-${r.llave}`}>Servicio</label>
                  <select
                    id={`servicio-${r.llave}`}
                    value={r.servicioReparacionId}
                    onChange={(e) => elegirServicio(r.llave, e.target.value)}
                  >
                    <option value="">Capturar a mano…</option>
                    {servicios.map((s) => (
                      <option key={s.id} value={s.id}>{s.codigo} — {s.descripcion}</option>
                    ))}
                  </select>
                  <span className="ayuda">
                    Del catálogo que mantiene Compras. Trae los precios de
                    referencia y se pueden corregir aquí sin tocarlo.
                  </span>
                </div>
              ) : (
                <BuscadorRefaccion
                  id={`refaccion-${r.llave}`}
                  valor={r.refaccion}
                  onElegir={(f) => elegirRefaccion(r.llave, f)}
                />
              )}

              <div className="campo">
                <label htmlFor={`descripcion-${r.llave}`}>Descripción *</label>
                <input
                  id={`descripcion-${r.llave}`} value={r.descripcion} maxLength={300}
                  onChange={(e) => cambiarRenglon(r.llave, 'descripcion', e.target.value)}
                  placeholder="Qué se va a hacer"
                />
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div className="campo" style={{ maxWidth: 200 }}>
                  <label htmlFor={`cantidad-${r.llave}`}>Cantidad *</label>
                  <input
                    id={`cantidad-${r.llave}`} type="number" min="0.01" step="0.01"
                    value={r.cantidad}
                    onChange={(e) => cambiarRenglon(r.llave, 'cantidad', e.target.value)}
                  />
                </div>

                {/* El costo, sólo en los servicios y sólo el costo.
                    Quien registra habló con el taller y tiene el número
                    enfrente; pedírselo a Operaciones era pedirle que lo
                    buscara otra vez. El de una pieza no cabe aquí: sale
                    del inventario, que Almacén tiene enfrente y esta
                    pantalla no. Y la venta tampoco: es el precio al
                    cliente y la pone quien autoriza. */}
                {r.tipo === 'servicio' && (
                  <div className="campo" style={{ maxWidth: 200 }}>
                    <label htmlFor={`costo-${r.llave}`}>Costo</label>
                    <input
                      id={`costo-${r.llave}`} type="number" min="0" step="0.01"
                      value={r.precioCosto}
                      placeholder="Lo que nos cuesta"
                      onChange={(e) => cambiarRenglon(r.llave, 'precioCosto', e.target.value)}
                    />
                  </div>
                )}

                {/* A quién se le paga.
                    Va junto al costo porque es la otra mitad del mismo
                    dato: acabas de escribir cuánto, y esto es a quién. De
                    aquí nace el vale TOT, y sin proveedor no se puede
                    generar. Los técnicos de la casa están dados de alta
                    como proveedores justo para esto. */}
                {r.tipo === 'servicio' && (
                  <div className="campo" style={{ minWidth: 220 }}>
                    <label htmlFor={`prov-${r.llave}`}>Se le paga a</label>
                    <select
                      id={`prov-${r.llave}`}
                      value={r.proveedorId}
                      onChange={(e) => cambiarRenglon(r.llave, 'proveedorId', e.target.value)}
                    >
                      <option value="">Escoge a quién</option>
                      {proveedores.map((p) => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/*
                Quién paga este concepto, en el concepto.
                Va debajo del costo y antes de la ayuda porque es la otra
                mitad de la pregunta del dinero: acabas de decir lo que
                nos cuesta, y esto dice si se lo cobramos al cliente.
                Es una casilla y no dos radios —al contrario que la
                pregunta de la orden que reemplaza— porque aquí sí hay un
                valor propuesto y visible: la matriz lo puso, el texto de
                abajo dice que lo puso, y desmarcarla es contradecirla a
                la vista. Lo que no se puede es que nadie conteste, y eso
                ya no pasa: siempre hay una respuesta en pantalla.
              */}
              <label style={{ display: 'block', marginTop: 8, fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={r.cubiertoPorIguala}
                  onChange={(e) => setRenglones((rs) => rs.map((x) => (
                    x.llave === r.llave
                      ? { ...x, cubiertoPorIguala: e.target.checked }
                      : x
                  )))}
                />{' '}
                <strong>Lo cubre la iguala</strong> · no se le cobra al cliente
              </label>

              <span className="ayuda">
                {r.cubiertoPorIguala
                  ? 'Sin precio de venta: la iguala ya lo paga, y cobrarlo'
                    + ' aparte sería cobrarlo dos veces. El trabajo se paga'
                    + ' igual, con su vale TOT.'
                  : r.tipo === 'refaccion'
                    ? 'Almacén le pondrá costo y venta al autorizarla, y esa'
                      + ' venta es lo que se le factura al cliente.'
                    : 'El costo se puede dejar en blanco si todavía no se sabe;'
                      + ' a quién se le paga, no: de ahí sale el vale TOT.'
                      + ' El precio de venta lo pone Operaciones al autorizar.'}
              </span>

              {renglones.length > 1 && (
                <button
                  type="button"
                  onClick={() => setRenglones((rs) => rs.filter((x) => x.llave !== r.llave))}
                >
                  Quitar renglón {i + 1}
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() => setRenglones((rs) => [...rs, {
              ...renglonVacio(),
              // El renglón nuevo nace con lo que propone la matriz, igual
              // que los de la carga inicial: si en esta tienda y esta área
              // la iguala cubre, llega marcado. `costoQueProponeLaMatriz`
              // dice si **se cobra**, que es al revés que la casilla.
              cubiertoPorIguala: !costoQueProponeLaMatriz,
            }])}
          >
            Agregar renglón
          </button>
        </>
      )}

      {pide('precio') && (
        <div className="campo">
          <label htmlFor="precioAutorizado">Precio autorizado por el cliente *</label>
          <input
            id="precioAutorizado" type="number" min="0" step="0.01" value={precio}
            onChange={(e) => setPrecio(e.target.value)}
          />
          <span className="ayuda">Es lo que se va a facturar.</span>
        </div>
      )}

      {/*
        El motivo, y el caso que lo pide sin que la transición lo exija.

        Editar una cotización con firmas le deshace el trabajo a quien
        autorizó, y la API pide que se explique. Sin este campo a la
        vista, guardar devolvía un error pidiendo un motivo que la
        pantalla no ofrecía dónde escribir.
      */}
      {(pide('motivo') || (editandoCotizacion && firmados > 0)) && (
        <div className="campo">
          <label htmlFor="motivo">Motivo *</label>
          <textarea
            id="motivo" rows={3} value={motivo}
            placeholder="Queda en la bitácora de la orden. Mínimo 5 caracteres."
            onChange={(e) => setMotivo(e.target.value)}
          />
          {editandoCotizacion && firmados > 0 && (
            <span className="ayuda">
              Es lo que va a leer quien tenga que autorizar otra vez.
            </span>
          )}
        </div>
      )}
    </Dialogo>
  );
}

// ═══════════════════════════════════════════════════ corregir datos

/**
 * Arreglar lo que se capturó mal.
 *
 * No es una transición: no mueve el estatus ni pide autorización, sólo
 * corrige. Existe porque una carga masiva de trescientas órdenes trae
 * determinantes cruzados y folios mal tecleados, y sin esto la única
 * salida era cancelar y recapturar —perdiendo la bitácora, las
 * refacciones pedidas y la evidencia que ya cuelgan de esa orden—.
 *
 * El folio y la sucursal se pueden cambiar y no se piden a la ligera: el
 * folio es con el que el cliente se refiere a la orden, y mover la
 * sucursal arrastra región, estado y tipo de iguala. Todo cambio queda en
 * la bitácora con lo que había antes.
 */
function Corregir({
  orden, onCerrar, onHecho,
}: {
  orden: OrdenDetalle;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [folio, setFolio] = useState(orden.folio);
  const [sucursalId, setSucursalId] = useState(orden.sucursal?.id ?? '');
  const [busqueda, setBusqueda] = useState('');
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [tipo, setTipo] = useState(orden.tipo);
  const [prioridad, setPrioridad] = useState(orden.prioridad);
  const [descripcionFalla, setDescripcionFalla] = useState(orden.descripcionFalla ?? '');
  const [noOsCliente, setNoOsCliente] = useState(orden.noOsCliente ?? '');
  const [equipoTexto, setEquipoTexto] = useState(orden.equipoTexto ?? '');
  const [contactoSitio, setContactoSitio] = useState(orden.contactoSitio ?? '');
  const [responsableId, setResponsableId] = useState(orden.responsable?.id ?? '');
  const [responsables, setResponsables] = useState<OpcionResponsable[]>([]);

  // Cascada del equipo: área → tipo → marca → modelo. Cada nivel filtra
  // al siguiente, así que cambiar uno borra los de abajo: un modelo que
  // no es de la marca elegida no es un dato, es un error guardado.
  const [areaId, setAreaId] = useState(orden.area?.id ?? '');
  const [tipoEquipoId, setTipoEquipoId] = useState(orden.tipoEquipo?.id ?? '');
  const [marcaId, setMarcaId] = useState(orden.marca?.id ?? '');
  const [modeloId, setModeloId] = useState(orden.modelo?.id ?? '');
  const [areas, setAreas] = useState<Opcion[]>([]);
  const [tiposEquipo, setTiposEquipo] = useState<Opcion[]>([]);
  const [marcas, setMarcas] = useState<Opcion[]>([]);
  const [modelos, setModelos] = useState<Opcion[]>([]);

  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/areas/opciones').then(setAreas).catch(() => undefined);
  }, []);

  // Acotados a la región de la orden, igual que en el alta: quien
  // responde por una orden de Bajío sale de Bajío. La lista llega con
  // los supervisores arriba y su zona al lado.
  useEffect(() => {
    api.get<OpcionResponsable[]>('/usuarios/opciones', {
      regionId: orden.region?.id || undefined,
    }).then(setResponsables).catch(() => setResponsables([]));
  }, [orden.region?.id]);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/tipos-equipo/opciones', { area_id: areaId || undefined })
      .then(setTiposEquipo).catch(() => setTiposEquipo([]));
  }, [areaId]);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/marcas/opciones', {
      tipo_equipo_id: tipoEquipoId || undefined,
    }).then(setMarcas).catch(() => setMarcas([]));
  }, [tipoEquipoId]);

  useEffect(() => {
    if (!marcaId) { setModelos([]); return; }
    api.get<Opcion[]>('/catalogos/modelos/opciones', {
      marca_id: marcaId, tipo_equipo_id: tipoEquipoId || undefined,
    }).then(setModelos).catch(() => setModelos([]));
  }, [marcaId, tipoEquipoId]);

  // Son 365 sucursales: se buscan, no se listan. Sin escribir nada se
  // deja la que ya tiene, que es el caso normal.
  useEffect(() => {
    if (busqueda.trim().length < 2) { setSucursales([]); return undefined; }
    const t = setTimeout(() => {
      api.get<Pagina<Sucursal>>('/sucursales', {
        busqueda: busqueda.trim(), estado: 'activo', limite: 20,
      })
        .then((r) => setSucursales(r.data))
        .catch(() => setSucursales([]));
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const elegida = sucursales.find((s) => s.id === sucursalId);
  const cambiaSucursal = sucursalId !== (orden.sucursal?.id ?? '');

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.patch(`/ordenes/${orden.id}`, {
        folio: folio.trim() !== orden.folio ? folio.trim() : undefined,
        sucursalId: cambiaSucursal ? sucursalId : undefined,
        tipo,
        prioridad,
        // Null y no cadena vacía: es lo que borra la falla de una
        // preventiva a la que alguien le puso texto por trámite.
        descripcionFalla: descripcionFalla.trim() || null,
        noOsCliente: noOsCliente.trim(),
        equipoTexto: equipoTexto.trim(),
        contactoSitio: contactoSitio.trim(),
        // Null y no cadena vacía: dejar la orden sin responsable es una
        // decisión, y con '' el DTO rechazaría el UUID.
        responsableId: responsableId || null,
        // Vacío es una respuesta: quitar el modelo que estaba mal y
        // dejarlo sin poner es corregir. Por eso van como null y no se
        // omiten.
        areaId: areaId || null,
        tipoEquipoId: tipoEquipoId || null,
        marcaId: marcaId || null,
        modeloId: modeloId || null,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Corregir la orden ${folioOT(orden.folio)}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            className="primario" onClick={guardar}
            disabled={trabajando || folio.trim().length < 3}
          >
            {trabajando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <p className="sub">
        Todo cambio queda en la bitácora con el valor anterior.
      </p>

      <div className="rejilla-datos">
        <div className="campo">
          <label htmlFor="folioCorregir">Folio OT *</label>
          <div className="controles">
            <span className="mono" style={{ fontWeight: 600 }}>OT-</span>
            <input
              id="folioCorregir" className="mono" value={folio} maxLength={20}
              style={{ flex: 1 }}
              onChange={(e) => setFolio(e.target.value)}
            />
          </div>
          <span className="ayuda">
            El número con el que el cliente la identifica. Los ceros de la
            izquierda cuentan.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="noOsCorregir">No. de orden de servicio</label>
          <input
            id="noOsCorregir" className="mono" value={noOsCliente} maxLength={50}
            onChange={(e) => setNoOsCliente(e.target.value)}
          />
        </div>

        <div className="campo">
          <label htmlFor="tipoCorregir">Tipo</label>
          <select id="tipoCorregir" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="correctivo">Correctivo</option>
            <option value="preventivo">Preventivo</option>
          </select>
        </div>

        <div className="campo">
          <label htmlFor="prioridadCorregir">Prioridad</label>
          <select
            id="prioridadCorregir" value={prioridad}
            onChange={(e) => setPrioridad(e.target.value)}
          >
            <option value="baja">Baja</option>
            <option value="media">Media</option>
            <option value="alta">Alta</option>
            <option value="urgente">Urgente</option>
          </select>
          <span className="ayuda">Cambiarla recalcula la fecha límite.</span>
        </div>

        <div className="campo">
          <label htmlFor="contactoCorregir">Contacto en sitio</label>
          <input
            id="contactoCorregir" value={contactoSitio} maxLength={150}
            onChange={(e) => setContactoSitio(e.target.value)}
          />
        </div>

        {/* Quién responde por la orden. Se fijaba al crear y no se
            volvía a tocar nunca, y cambia por motivos normales: un
            supervisor de vacaciones, un reparto distinto de la zona, o
            una orden que se cargó cuando su zona todavía no tenía
            responsable declarado. Karin: «y que ese campo sea editable,
            cuando sea necesario». El cambio queda en la bitácora de la
            orden con el nombre de quien entra. */}
        <div className="campo">
          <label htmlFor="responsableCorregir">Responsable</label>
          <select
            id="responsableCorregir" value={responsableId}
            onChange={(e) => setResponsableId(e.target.value)}
          >
            <option value="">Sin responsable</option>
            <OpcionesDeResponsable responsables={responsables} />
          </select>
          <span className="ayuda">
            Quien da seguimiento. Los supervisores de la zona, primero.
          </span>
        </div>

        <div className="campo ancho-total">
          <label htmlFor="fallaCorregir">Falla reportada</label>
          <textarea
            id="fallaCorregir" rows={2} value={descripcionFalla} maxLength={2000}
            onChange={(e) => setDescripcionFalla(e.target.value)}
          />
          {/* Se puede vaciar. En una preventiva no hay falla que
              reportar, y borrar el texto que alguien puso por trámite es
              corregir: se guarda nula. */}
          <span className="ayuda">
            {tipo === 'preventivo'
              ? 'Opcional en una preventiva. Déjala vacía si no hay nada que'
                + ' reportar; lo que se hace sale de los servicios pactados.'
              : 'Mínimo 10 caracteres si escribes algo.'}
          </span>
        </div>
      </div>

      <h2>Equipo</h2>

      <div className="rejilla-datos">
        <div className="campo">
          <label htmlFor="areaCorregir">Área</label>
          <select
            id="areaCorregir" value={areaId}
            onChange={(e) => {
              setAreaId(e.target.value);
              setTipoEquipoId(''); setMarcaId(''); setModeloId('');
            }}
          >
            <option value="">Sin área</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="tipoEquipoCorregir">Tipo de equipo</label>
          <select
            id="tipoEquipoCorregir" value={tipoEquipoId}
            onChange={(e) => {
              setTipoEquipoId(e.target.value);
              setMarcaId(''); setModeloId('');
            }}
          >
            <option value="">{areaId ? 'Equipos del área' : 'Todos los equipos'}</option>
            {tiposEquipo.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="marcaCorregir">Marca</label>
          <select
            id="marcaCorregir" value={marcaId}
            onChange={(e) => { setMarcaId(e.target.value); setModeloId(''); }}
          >
            <option value="">{tipoEquipoId ? 'Marcas de ese equipo' : 'Todas las marcas'}</option>
            {marcas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="modeloCorregir">Modelo</label>
          <select
            id="modeloCorregir" value={modeloId} disabled={!marcaId}
            onChange={(e) => setModeloId(e.target.value)}
          >
            <option value="">{marcaId ? 'Sin modelo' : 'Elige una marca primero'}</option>
            {modelos.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
        </div>

        <div className="campo ancho-total">
          <label htmlFor="equipoCorregir">Si el equipo no está en el catálogo, descríbelo</label>
          <input
            id="equipoCorregir" value={equipoTexto} maxLength={150}
            onChange={(e) => setEquipoTexto(e.target.value)}
          />
        </div>
      </div>

      <h2>Sucursal</h2>
      <p className="sub">
        Ahora: {orden.sucursal
          ? `${orden.sucursal.determinante} — ${orden.sucursal.nombre}`
          : 'sin sucursal'}
      </p>

      <div className="campo">
        <label htmlFor="buscarSucursal">Cambiarla</label>
        <input
          id="buscarSucursal" type="search" value={busqueda}
          placeholder="Determinante o nombre…"
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <div style={{ display: 'grid', gap: 4, maxHeight: 160, overflow: 'auto' }}>
          {sucursales.map((su) => (
            <button
              key={su.id} className="enlace" style={{ textAlign: 'left' }}
              onClick={() => setSucursalId(su.id)}
            >
              {su.id === sucursalId ? '✓ ' : '+ '}
              <span className="mono">{su.determinante}</span> {su.nombre}
            </button>
          ))}
        </div>
      </div>

      {cambiaSucursal && (
        <div className="aviso">
          Se moverá a <strong>{elegida ? `${elegida.determinante} — ${elegida.nombre}` : 'otra sucursal'}</strong>.
          {' '}Con ella cambian la región, el estado y el tipo de iguala, así que
          la orden pasa a la bandeja de esa zona y se cobra con esa iguala.
        </div>
      )}
    </Dialogo>
  );
}

// ══════════════════════════════════════════════════════════════ alta

function FormularioOrden({
  onCerrar, onCreada,
}: {
  onCerrar: () => void;
  onCreada: (id: string) => void;
}) {
  // Cascada del sitio: cliente → región → estado → sucursal.
  const [clientes, setClientes] = useState<Opcion[]>([]);
  const [regiones, setRegiones] = useState<Opcion[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [estados, setEstados] = useState<Opcion[]>([]);
  const [clienteId, setClienteId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [estadoId, setEstadoId] = useState('');

  // Cascada del equipo: área → tipo de equipo → marca → modelo.
  const [areas, setAreas] = useState<Opcion[]>([]);
  const [tiposEquipo, setTiposEquipo] = useState<Opcion[]>([]);
  const [marcas, setMarcas] = useState<Opcion[]>([]);
  const [modelos, setModelos] = useState<Opcion[]>([]);

  const [tecnicos, setTecnicos] = useState<Opcion[]>([]);
  const [responsables, setResponsables] = useState<OpcionResponsable[]>([]);

  /**
   * Un desplegable vacío puede significar dos cosas muy distintas: que no
   * hay datos, o que la petición falló. Antes se tragaban los errores y
   * las dos se veían igual, que es como se perdió una tarde buscando por
   * qué "no salen las regiones". Ahora cada carga deja su motivo.
   */
  const [fallos, setFallos] = useState<Record<string, string>>({});

  // Genérica en el tipo de opción: el responsable trae su rol y su zona,
  // y antes esto obligaba a que todo cupiera en `Opcion`.
  const cargarOpciones = useCallback(<T extends Opcion>(
    campo: string,
    ruta: string,
    parametros: Record<string, string | number | undefined>,
    aplicar: (valores: T[]) => void,
  ) => {
    api.get<T[]>(ruta, parametros)
      .then((valores) => {
        aplicar(valores);
        setFallos((previo) => {
          if (!(campo in previo)) return previo;
          const copia = { ...previo };
          delete copia[campo];
          return copia;
        });
      })
      .catch((e: unknown) => {
        aplicar([]);
        setFallos((previo) => ({
          ...previo,
          [campo]: motivoDeFalla(e),
        }));
      });
  }, []);

  const [datos, setDatos] = useState({
    sucursalId: '',
    responsableId: '',
    folio: '',
    noOsCliente: '',
    tipo: 'correctivo',
    prioridad: 'media',
    estatusEquipo: 'sin_especificar',
    descripcionFalla: '',
    areaId: '',
    tipoEquipoId: '',
    marcaId: '',
    modeloId: '',
    equipoTexto: '',
    contactoSitio: '',
    tecnicoId: '',
    fechaProgramada: '',
    tieneCosto: false,
  });
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    cargarOpciones('regiones', '/catalogos/regiones/opciones', {}, setRegiones);
    cargarOpciones('areas', '/catalogos/areas/opciones', {}, setAreas);

    api.get<Pagina<{ id: string; nombre: string }>>('/clientes', {
      limite: 100, estado: 'activo',
    })
      .then((r) => {
        setClientes(r.data);
        // Con una sola cadena no hay nada que elegir: se preselecciona.
        if (r.data.length === 1) setClienteId(r.data[0].id);
      })
      .catch((e: unknown) => {
        setClientes([]);
        setFallos((previo) => ({
          ...previo,
          clientes: motivoDeFalla(e),
        }));
      });
  }, [cargarOpciones]);

  // Los estados se acotan a la región: es el escalón intermedio de la
  // jerarquía región → estado → sucursal.
  useEffect(() => {
    cargarOpciones('estados', '/catalogos/estados/opciones', {
      region_id: regionId || undefined,
    }, setEstados);
  }, [regionId, cargarOpciones]);

  // Los tipos de equipo se acotan al área elegida.
  useEffect(() => {
    cargarOpciones('tiposEquipo', '/catalogos/tipos-equipo/opciones', {
      area_id: datos.areaId || undefined,
    }, setTiposEquipo);
  }, [datos.areaId, cargarOpciones]);

  // Las marcas se acotan al tipo de equipo a través de sus modelos: la
  // marca no guarda el tipo de equipo, son los modelos los que lo saben.
  useEffect(() => {
    cargarOpciones('marcas', '/catalogos/marcas/opciones', {
      tipo_equipo_id: datos.tipoEquipoId || undefined,
    }, setMarcas);
  }, [datos.tipoEquipoId, cargarOpciones]);

  useEffect(() => {
    if (!datos.marcaId) { setModelos([]); return; }
    cargarOpciones('modelos', '/catalogos/modelos/opciones', {
      marca_id: datos.marcaId,
      tipo_equipo_id: datos.tipoEquipoId || undefined,
    }, setModelos);
  }, [datos.marcaId, datos.tipoEquipoId, cargarOpciones]);

  // Son 365 sucursales. Al bajar por región y estado quedan unas pocas
  // por estado, así que el desplegable basta y no hace falta buscador.
  useEffect(() => {
    api.get<Pagina<Sucursal>>('/sucursales', {
      clienteId: clienteId || undefined,
      regionId: regionId || undefined,
      estadoId: estadoId || undefined,
      estado: 'activo',
      limite: 200,
    })
      .then((r) => {
        setSucursales(r.data);
        setFallos((previo) => {
          if (!('sucursales' in previo)) return previo;
          const copia = { ...previo };
          delete copia.sucursales;
          return copia;
        });
      })
      .catch((e: unknown) => {
        setSucursales([]);
        setFallos((previo) => ({
          ...previo,
          sucursales: motivoDeFalla(e),
        }));
      });
  }, [clienteId, regionId, estadoId]);

  // Técnicos y responsables se ofrecen de la región elegida: el técnico
  // porque es el criterio de despacho, el responsable porque quien da
  // seguimiento suele ser de la zona.
  useEffect(() => {
    cargarOpciones('tecnicos', '/tecnicos/opciones', {
      regionId: regionId || undefined,
    }, setTecnicos);

    cargarOpciones('responsables', '/usuarios/opciones', {
      regionId: regionId || undefined,
    }, setResponsables);
  }, [regionId, cargarOpciones]);

  /**
   * El responsable se deja vacío a propósito, y la API lo decide.
   *
   * Antes se prellenaba con quien captura. Desde la 0073 el responsable
   * de una orden sale de la zona de su sucursal —el supervisor que la
   * cubre— y prellenar el campo convertía eso en letra muerta: lo
   * capturado gana sobre la zona, así que el valor por omisión de la
   * pantalla habría ganado siempre.
   *
   * Vacío no significa «sin responsable»: significa «el que decida la
   * regla», que es el supervisor de la zona o, si esa zona no tiene uno
   * declarado, quien captura. Igual que antes, entonces, cuando el
   * catálogo está a medias.
   */

  function cambiar(cambios: Partial<typeof datos>) {
    setDatos((previo) => ({ ...previo, ...cambios }));
  }

  // La iguala no se captura: la trae la sucursal. Se muestra porque de
  // ella depende qué cubre el contrato y qué se cobra aparte.
  const sucursal = sucursales.find((s) => s.id === datos.sucursalId) ?? null;

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      const r = await api.post<{ id: string; folio: string }>('/ordenes', {
        sucursalId: datos.sucursalId,
        responsableId: datos.responsableId || undefined,
        folio: datos.folio.trim(),
        noOsCliente: datos.noOsCliente.trim() || undefined,
        tipo: datos.tipo,
        prioridad: datos.prioridad,
        estatusEquipo: datos.estatusEquipo,
        descripcionFalla: datos.descripcionFalla.trim(),
        areaId: datos.areaId || undefined,
        tipoEquipoId: datos.tipoEquipoId || undefined,
        marcaId: datos.marcaId || undefined,
        modeloId: datos.modeloId || undefined,
        equipoTexto: datos.equipoTexto.trim() || undefined,
        contactoSitio: datos.contactoSitio.trim() || undefined,
        tecnicoId: datos.tecnicoId || undefined,
        fechaProgramada: datos.fechaProgramada
          ? new Date(datos.fechaProgramada).toISOString()
          : undefined,
        tieneCosto: datos.tieneCosto,
      });
      onCreada(r.id);
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  /**
   * La falla sólo se exige en una correctiva.
   *
   * Ahí es el motivo de la orden: alguien reportó algo roto y sin decir
   * qué el técnico llega a ciegas. En una preventiva no hay nada que
   * reportar —es una visita del programa— y pedirla obligaba a inventar
   * texto. Un campo que se llena por trámite es peor que uno vacío,
   * porque quien lo lee no lo distingue de uno de verdad.
   *
   * Si se escribe algo en una preventiva, se le sigue pidiendo que diga
   * algo: opcional no es «cualquier cosa vale».
   */
  const esPreventiva = datos.tipo === 'preventivo';
  const falla = datos.descripcionFalla.trim();

  // Igual que en almacén: se dice qué falta en vez de dejar el botón gris.
  const faltantes: string[] = [];
  if (!datos.sucursalId) faltantes.push('elegir sucursal');
  if (falla.length > 0 ? falla.length < 10 : !esPreventiva) {
    faltantes.push('la descripción del trabajo, de al menos 10 caracteres');
  }

  const listo = faltantes.length === 0;

  return (
    <Dialogo
      titulo="Nueva orden de trabajo"
      ancho
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando || !listo}>
            {trabajando ? 'Creando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />
      <Faltantes que={faltantes} />

      <div className="rejilla-campos">
        {/* ── Sitio: cliente → región → estado → sucursal ────────── */}
        <h2 className="ancho-total seccion">Sitio</h2>

        <div className="campo">
          <label htmlFor="clienteId">Cliente *</label>
          <select
            id="clienteId" value={clienteId}
            onChange={(e) => {
              setClienteId(e.target.value);
              cambiar({ sucursalId: '' });
            }}
          >
            <option value="">Seleccione una opción</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
          <Falla mensaje={fallos.clientes} />
        </div>

        <div className="campo">
          <label htmlFor="regionId">Región</label>
          <select
            id="regionId" value={regionId}
            onChange={(e) => {
              setRegionId(e.target.value);
              setEstadoId('');
              cambiar({ sucursalId: '', tecnicoId: '' });
            }}
          >
            <option value="">Todas las regiones</option>
            {regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
          <Falla mensaje={fallos.regiones} />
          {!fallos.regiones && (
            <span className="ayuda">
              {regiones.length > 0
                ? 'Acota estados, sucursales, responsables y técnicos.'
                : 'No hay regiones activas en el catálogo.'}
            </span>
          )}
        </div>

        <div className="campo">
          <label htmlFor="estadoId">Estado</label>
          <select
            id="estadoId" value={estadoId}
            onChange={(e) => { setEstadoId(e.target.value); cambiar({ sucursalId: '' }); }}
          >
            <option value="">
              {regionId ? 'Todos los de la región' : 'Todos los estados'}
            </option>
            {estados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
          <Falla mensaje={fallos.estados} />
        </div>

        <div className="campo">
          <label htmlFor="sucursalId">Sucursal *</label>
          <select
            id="sucursalId" value={datos.sucursalId}
            onChange={(e) => cambiar({ sucursalId: e.target.value })}
          >
            <option value="">Seleccione una opción</option>
            {sucursales.map((s) => (
              <option key={s.id} value={s.id}>
                {s.determinante} — {s.nombre}
              </option>
            ))}
          </select>
          <Falla mensaje={fallos.sucursales} />
          {!fallos.sucursales && (
            <span className="ayuda">
              {sucursales.length} {sucursales.length === 1 ? 'sucursal' : 'sucursales'}
              {!estadoId && ' · elige estado para acotar'}
            </span>
          )}
        </div>

        <div className="campo">
          <label>Iguala</label>
          <input value={sucursal?.tipoIguala?.nombre ?? '—'} readOnly tabIndex={-1} />
          <span className="ayuda">La define la sucursal; aquí no se cambia.</span>
        </div>

        <div className="campo">
          <label htmlFor="contactoSitio">Contacto en sitio</label>
          <input
            id="contactoSitio" value={datos.contactoSitio} maxLength={150}
            placeholder={sucursal?.contactoSitio ?? ''}
            onChange={(e) => cambiar({ contactoSitio: e.target.value })}
          />
        </div>

        {/* ── Folios y clasificación ─────────────────────────────── */}
        <h2 className="ancho-total seccion">Folios y clasificación</h2>

        <div className="campo">
          <label htmlFor="folio">No. de orden de trabajo *</label>
          {/* El «OT» va fuera del campo: se muestra, no se captura ni se
              guarda. Adentro va el número tal como lo emitió el cliente,
              ceros de la izquierda incluidos. */}
          <div className="controles">
            <span className="mono" style={{ fontWeight: 600 }}>OT-</span>
            <input
              id="folio" className="mono" value={datos.folio} maxLength={20}
              style={{ flex: 1 }}
              onChange={(e) => cambiar({ folio: e.target.value })}
            />
          </div>
          <span className="ayuda">
            El folio del cliente, tal cual. Si empieza con ceros, escríbelos:
            0012345 no es la misma orden que 12345.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="noOsCliente">No. de orden de servicio</label>
          <input
            id="noOsCliente" className="mono" value={datos.noOsCliente} maxLength={50}
            onChange={(e) => cambiar({ noOsCliente: e.target.value })}
          />
        </div>

        <div className="campo">
          <label htmlFor="tipo">Tipo de orden</label>
          <select id="tipo" value={datos.tipo} onChange={(e) => cambiar({ tipo: e.target.value })}>
            <option value="correctivo">Correctivo</option>
            <option value="preventivo">Preventivo</option>
          </select>
        </div>

        <div className="campo">
          <label htmlFor="prioridad">Prioridad</label>
          <select
            id="prioridad" value={datos.prioridad}
            onChange={(e) => cambiar({ prioridad: e.target.value })}
          >
            <option value="urgente">Urgente</option>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>
          <span className="ayuda">De aquí sale la fecha límite.</span>
        </div>

        {/* ── Equipo: área → tipo → marca → modelo ──────────────── */}
        <h2 className="ancho-total seccion">Equipo</h2>

        <div className="campo">
          <label htmlFor="areaId">Área</label>
          <select
            id="areaId" value={datos.areaId}
            onChange={(e) => cambiar({
              areaId: e.target.value, tipoEquipoId: '', marcaId: '', modeloId: '',
            })}
          >
            <option value="">Seleccione una opción</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
          <Falla mensaje={fallos.areas} />
        </div>

        <div className="campo">
          <label htmlFor="tipoEquipoId">Equipo</label>
          <select
            id="tipoEquipoId" value={datos.tipoEquipoId}
            onChange={(e) => cambiar({
              tipoEquipoId: e.target.value, marcaId: '', modeloId: '',
            })}
          >
            <option value="">{datos.areaId ? 'Equipos del área' : 'Todos los equipos'}</option>
            {tiposEquipo.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
          <Falla mensaje={fallos.tiposEquipo} />
        </div>

        <div className="campo">
          <label htmlFor="marcaId">Marca</label>
          <select
            id="marcaId" value={datos.marcaId}
            onChange={(e) => cambiar({ marcaId: e.target.value, modeloId: '' })}
          >
            <option value="">
              {datos.tipoEquipoId ? 'Marcas de ese equipo' : 'Todas las marcas'}
            </option>
            {marcas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
          <Falla mensaje={fallos.marcas} />
        </div>

        <div className="campo">
          <label htmlFor="modeloId">Modelo</label>
          <select
            id="modeloId" value={datos.modeloId} disabled={!datos.marcaId}
            onChange={(e) => cambiar({ modeloId: e.target.value })}
          >
            <option value="">{datos.marcaId ? 'Seleccione una opción' : 'Elige una marca'}</option>
            {modelos.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select>
          <Falla mensaje={fallos.modelos} />
        </div>

        <div className="campo">
          <label htmlFor="estatusEquipo">Estatus del equipo</label>
          <select
            id="estatusEquipo" value={datos.estatusEquipo}
            onChange={(e) => cambiar({ estatusEquipo: e.target.value })}
          >
            <option value="sin_especificar">Sin especificar</option>
            <option value="fuera_de_servicio">Fuera de servicio</option>
            <option value="en_servicio">En servicio</option>
          </select>
        </div>

        <div className="campo">
          <label htmlFor="equipoTexto">Equipo (texto libre)</label>
          <input
            id="equipoTexto" value={datos.equipoTexto} maxLength={150}
            placeholder="Si no está en el catálogo"
            onChange={(e) => cambiar({ equipoTexto: e.target.value })}
          />
        </div>

        {/* ── Asignación y programación ─────────────────────────── */}
        <h2 className="ancho-total seccion">Asignación</h2>

        <div className="campo">
          <label htmlFor="responsableId">Responsable</label>
          <select
            id="responsableId" value={datos.responsableId}
            onChange={(e) => cambiar({ responsableId: e.target.value })}
          >
            <option value="">El supervisor de la zona</option>
            <OpcionesDeResponsable responsables={responsables} />
          </select>
          <Falla mensaje={fallos.responsables} />
          {!fallos.responsables && (
            <span className="ayuda">
              Sin elegir nada, la orden nace a nombre de quien cubre la zona de
              la sucursal. Si esa zona no tiene responsable declarado, queda a
              tu nombre.
            </span>
          )}
        </div>

        <div className="campo">
          <label htmlFor="tecnicoId">Técnico</label>
          <select
            id="tecnicoId" value={datos.tecnicoId}
            onChange={(e) => cambiar({ tecnicoId: e.target.value })}
          >
            <option value="">Asignar después</option>
            {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
          <Falla mensaje={fallos.tecnicos} />
          {!fallos.tecnicos && (
            <span className="ayuda">
              {regionId
                ? `${tecnicos.length} en la región. Si lo eliges, la orden nace asignada.`
                : 'Si lo eliges, la orden nace asignada.'}
            </span>
          )}
        </div>

        <div className="campo">
          <label htmlFor="fechaProgramada">Fecha programada</label>
          <input
            id="fechaProgramada" type="datetime-local" value={datos.fechaProgramada}
            onChange={(e) => cambiar({ fechaProgramada: e.target.value })}
          />
        </div>

        <div className="campo inline">
          <input
            id="tieneCosto" type="checkbox" checked={datos.tieneCosto}
            onChange={(e) => cambiar({ tieneCosto: e.target.checked })}
          />
          <label htmlFor="tieneCosto">
            Genera cargo adicional a la iguala
          </label>
        </div>

        <div className="campo ancho-total">
          <label htmlFor="descripcionFalla">
            Descripción del trabajo{esPreventiva ? '' : ' *'}
          </label>
          <textarea
            id="descripcionFalla" rows={4} value={datos.descripcionFalla} maxLength={1000}
            onChange={(e) => cambiar({ descripcionFalla: e.target.value })}
            placeholder={esPreventiva
              ? 'Opcional. Lo que el técnico deba saber antes de ir.'
              : undefined}
          />
          <span className="ayuda">
            {datos.descripcionFalla.length} / 1000 ·{' '}
            {esPreventiva
              /* En una preventiva nadie reportó una falla: es una visita
                 del programa y lo que se hace sale de los servicios
                 pactados. Se deja el campo por si hay algo que avisar. */
              ? 'Opcional en una preventiva: no hay falla reportada, lo que'
                + ' se hace sale de los servicios pactados de la sucursal.'
                + ' Si escribes algo, que sean al menos 10 caracteres.'
              : 'Mínimo 10 caracteres: es lo que lee el técnico antes de ir.'}
          </span>
        </div>
      </div>
    </Dialogo>
  );
}

/**
 * Pide una refacción para esta orden.
 *
 * Se puede pedir del catálogo o describir lo que haga falta: forzar a que
 * la pieza exista antes de poder pedirla es lo que hace que el técnico
 * mande la solicitud por WhatsApp.
 *
 * Al guardar, la orden pasa sola a «espera refacciones» y queda anotado
 * en su bitácora. No es una transición que alguien elige: es lo que de
 * verdad está pasando con esa orden.
 */
/**
 * Lo que esta orden le ha pedido a almacén, y en qué va.
 *
 * Sin esto, quien pidió una pieza no tiene forma de saber si almacén ya
 * la surtió, si la escaló a compras o si nadie la ha mirado, y acaba
 * preguntando por teléfono —que es exactamente el trabajo que el sistema
 * viene a quitar.
 */
function RefaccionesDeLaOrden({
  ordenId, recarga,
}: {
  ordenId: string;
  recarga: number;
}) {
  const [solicitudes, setSolicitudes] = useState<SolicitudAlmacen[] | null>(null);

  useEffect(() => {
    api.get<SolicitudAlmacen[]>(`/solicitudes-almacen/de-la-orden/${ordenId}`)
      .then(setSolicitudes)
      .catch(() => setSolicitudes([]));
  }, [ordenId, recarga]);

  if (solicitudes === null) return <Cargando />;
  if (solicitudes.length === 0) {
    return <p className="sub">Todavía no se ha pedido ninguna refacción.</p>;
  }

  return (
    <>
      {solicitudes.map((s) => (
        <div key={s.id} style={{ marginBottom: 12 }}>
          <p>
            <strong className="mono">{s.folio}</strong>{' '}
            <EtiquetaSolicitud valor={s.estatus} />
          </p>
          <table>
            <thead>
              <tr>
                <th>Pieza</th>
                <th>Nº de parte</th>
                <th className="numero">Pedidas</th>
                <th className="numero">Surtidas</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {s.partidas.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.refaccion
                      ? <>
                          <span className="mono">{p.refaccion.codigo}</span> — {p.refaccion.nombre}
                        </>
                      : <span className="conteo">{p.descripcionLibre}</span>}
                  </td>
                  <td className="mono">{p.refaccion?.numero_parte ?? '—'}</td>
                  <td className="numero">{p.cantidadSolicitada}</td>
                  <td className="numero">{p.cantidadSurtida}</td>
                  <td>
                    {/* Toda la cadena de la pieza en una celda: qué se
                        pidió, en qué compra va y cuándo se espera. Sin la
                        orden de compra, «en compras» no distingue entre
                        una solicitud que nadie ha atendido y mercancía que
                        llega mañana. */}
                    {p.compra
                      ? (
                        <>
                          <div className="conteo mono">{p.compra.folio}</div>
                          {p.compra.ordenCompra
                            ? (
                              <>
                                <div>
                                  <span className="mono">{p.compra.ordenCompra.folio}</span>
                                  {' · '}
                                  {ETIQUETAS_COMPRA[p.compra.ordenCompra.estatus]
                                    ?? p.compra.ordenCompra.estatus}
                                </div>
                                {p.compra.ordenCompra.fechaEntregaEstimada && (
                                  <div className="ayuda">
                                    Entrega estimada{' '}
                                    {fechaCorta(p.compra.ordenCompra.fechaEntregaEstimada)}
                                  </div>
                                )}
                              </>
                            )
                            : <div className="ayuda">Pedida a compras, sin orden todavía</div>}
                        </>
                      )
                      : <EtiquetaSolicitud valor={p.estatus} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

/** En qué va la orden de compra que trae la pieza. */
const ETIQUETAS_COMPRA: Record<string, string> = {
  borrador: 'en borrador',
  pendiente_autorizacion: 'por autorizar',
  autorizada: 'autorizada',
  rechazada: 'rechazada',
  enviada_proveedor: 'con el proveedor',
  recibida_parcial: 'recibida a medias',
  recibida_total: 'recibida en almacén',
  pagada: 'recibida y pagada',
  cancelada: 'cancelada',
};

const ETIQUETAS_SOLICITUD: Record<string, { texto: string; clase: string }> = {
  en_revision: { texto: 'En revisión', clase: '' },
  disponible: { texto: 'Hay existencia', clase: 'si' },
  parcial: { texto: 'Surtida a medias', clase: '' },
  sin_stock: { texto: 'Sin existencia', clase: 'no' },
  surtida: { texto: 'Surtida', clase: 'si' },
  cancelada: { texto: 'Cancelada', clase: 'no' },
};

function EtiquetaSolicitud({ valor }: { valor: string }) {
  const e = ETIQUETAS_SOLICITUD[valor] ?? { texto: valor, clase: '' };
  return <span className={`etiqueta ${e.clase}`}>{e.texto}</span>;
}

/**
 * Pide refacciones para esta orden.
 *
 * Se le pide a **almacén**, no a compras: comprar es lo que se hace
 * cuando almacén no la tiene. Van varias piezas en una sola solicitud
 * porque así llega el técnico —«necesito esto, esto y esto»— y partirlo
 * en tres solicitudes sólo multiplica el trabajo de quien surte.
 *
 * Al guardar, la orden pasa sola a esperar refacciones.
 */
function SolicitarRefaccion({
  ordenId, sucursalId, onSolicitada,
}: {
  ordenId: string;
  /**
   * La tienda de la orden, para avisar si ya se le surtió esta pieza.
   *
   * Karin: «para no caer en que soliciten una manguera 3 dias despues de
   * haberlo hecho». El histórico vive en Almacén, pero un reporte sólo
   * sirve si alguien se acuerda de abrirlo: el aviso aparece aquí, que es
   * donde se está a punto de pedirla dos veces.
   */
  sucursalId: string | null;
  onSolicitada: () => void;
}) {
  // Sin texto libre: aquí sólo entra el catálogo. Karin: «solicitar
  // refacciones directas no permitas pedir refacciones que no vengan en
  // catalogo». Una pieza descrita a mano no tiene código, así que no
  // tiene existencia, ni mínimo, ni proveedor, y el aviso de «ya se le
  // surtió hace poco» no la empareja con nada.
  const [lineas, setLineas] = useState<{
    refaccion: Refaccion | null; cantidad: string;
  }[]>([{ refaccion: null, cantidad: '1' }]);
  const [notas, setNotas] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [hecho, setHecho] = useState<string | null>(null);

  function cambiar(i: number, parcial: Partial<(typeof lineas)[number]>) {
    setLineas((antes) => antes.map((l, n) => (n === i ? { ...l, ...parcial } : l)));
  }

  const completas = lineas.filter((l) => l.refaccion);

  const faltantes: string[] = [];
  if (completas.length === 0) {
    faltantes.push('elegir al menos una refacción del catálogo');
  }

  async function solicitar() {
    setError(null);
    setTrabajando(true);
    try {
      const creada = await api.post<{ folio: string }>('/solicitudes-almacen', {
        ordenTrabajoId: ordenId,
        notasParaAlmacen: notas.trim() || undefined,
        partidas: completas.map((l) => ({
          refaccionId: l.refaccion!.id,
          cantidad: Number(l.cantidad) || 1,
        })),
      });
      setHecho(creada.folio);
      setLineas([{ refaccion: null, cantidad: '1' }]);
      setNotas('');
      onSolicitada();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <>
      <Error error={error} />

      {hecho && (
        <p className="etiqueta si">
          Solicitud {hecho} enviada a almacén. La orden queda esperando refacciones.
        </p>
      )}

      {lineas.map((linea, i) => (
        <div key={i} className="panel" style={{ marginBottom: 8 }}>
          <BuscadorRefaccion
            id={`refaccion-linea-${i}`}
            valor={linea.refaccion}
            onElegir={(r) => cambiar(i, { refaccion: r })}
          />

          {/* Y si ya se le surtió hace poco, se dice aquí mismo. Avisa y
              no impide: hay razones de sobra para pedir dos veces la misma
              pieza —se rompió otra, la anterior salió mala, son dos
              equipos— y bloquear obligaría a inventar cómo saltarse el
              bloqueo. Lo que evita es pedirla por olvido. */}
          <YaSeLeSurtio sucursalId={sucursalId} refaccion={linea.refaccion} />

          {/* Antes aquí había un campo para describir la pieza que no
              estuviera catalogada. Lo que entraba por ahí no servía
              después: almacén no podía decir si la tenía —así que la
              escalaba siempre—, compras no sabía a quién pedirla, el
              inventario no la contaba y el histórico de surtido no la
              emparejaba. En su lugar se dice qué hacer. */}
          {!linea.refaccion && (
            <div className="ayuda">
              Sólo se piden refacciones del catálogo. Si la pieza no está,
              pídele a almacén que la dé de alta y vuelve aquí: así queda con
              su código, su existencia y su proveedor.
            </div>
          )}

          <div className="campo">
            <label htmlFor={`cantidad-${i}`}>Cantidad</label>
            <input
              id={`cantidad-${i}`} type="number" min={1} value={linea.cantidad}
              onChange={(e) => cambiar(i, { cantidad: e.target.value })}
            />
          </div>

          {lineas.length > 1 && (
            <button
              className="enlace"
              onClick={() => setLineas((antes) => antes.filter((_, n) => n !== i))}
            >
              Quitar esta pieza
            </button>
          )}
        </div>
      ))}

      <button
        onClick={() => setLineas((antes) => [
          ...antes, { refaccion: null, cantidad: '1' },
        ])}
      >
        Agregar otra pieza
      </button>

      <div className="campo">
        <label htmlFor="notasAlmacen">Notas para almacén</label>
        <textarea
          id="notasAlmacen" value={notas} maxLength={1000} rows={2}
          onChange={(e) => setNotas(e.target.value)}
        />
      </div>

      {faltantes.length > 0 && <Faltantes que={faltantes} />}

      <button
        className="primario"
        onClick={() => void solicitar()}
        disabled={trabajando || faltantes.length > 0}
      >
        {trabajando ? 'Enviando…' : `Pedir a almacén ${completas.length} ${completas.length === 1 ? 'pieza' : 'piezas'}`}
      </button>
    </>
  );
}

function Dato({ etiqueta, valor, detalle }: {
  etiqueta: string;
  valor: string | null;
  /**
   * El porqué, debajo y en gris.
   *
   * Para los datos cuya respuesta es corta pero necesita sostenerse: el
   * valor contesta de un vistazo y el detalle está ahí para quien
   * pregunte. Metido todo en `valor` se convierte en un párrafo, y un
   * párrafo en una rejilla de datos ya no se lee como un dato.
   */
  detalle?: string | null;
}) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <span>{valor ?? '—'}</span>
      {detalle && <span className="ayuda">{detalle}</span>}
    </div>
  );
}

/**
 * Si a esta tienda ya se le surtió esta pieza hace poco.
 *
 * Karin: «para no caer en que soliciten una manguera 3 dias despues de
 * haberlo hecho».
 *
 * Sale de la misma consulta que el histórico de Almacén, acotada a una
 * refacción: dos lecturas del mismo hecho acaban diciendo cosas distintas,
 * y entonces ninguna sirve para decidir si hace falta pedirla.
 *
 * Calla cuando no hay nada que decir, y también cuando la pieza se
 * capturó libre: sin refacción de catálogo no hay con qué reconocerla
 * —«manguera 1/2"» y «MANGUERA DE 1/2 PULGADA» son la misma y no se
 * parecen— y un aviso al azar es peor que ninguno.
 */
function YaSeLeSurtio({ sucursalId, refaccion }: {
  sucursalId: string | null;
  refaccion: Refaccion | null;
}) {
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    setAviso(null);
    if (!sucursalId || !refaccion) return;

    api.get<AvisoDeSurtido>('/almacen/surtido/reciente', {
      sucursalId, refaccionId: refaccion.id,
    })
      // En silencio: esto es una ayuda, y que falle no puede impedir que
      // se pida la pieza.
      .then((r) => setAviso(r.aviso))
      .catch(() => setAviso(null));
  }, [sucursalId, refaccion]);

  if (!aviso) return null;

  return (
    <div className="aviso" style={{ marginTop: 6 }}>
      <strong>Ya se le surtió hace poco.</strong> {aviso} Revísalo antes de
      volver a pedirla.
    </div>
  );
}
